/**
 * @fileoverview Opportunities Table Component
 * @module components/opportunities/OpportunitiesTable
 * @description A comprehensive table component for displaying and managing medication opportunities.
 * Supports sorting, filtering, and various actions like editing and discontinuing medications.
 *
 *
 * @requires react
 * @requires @mui/material
 * @requires @mui/material/styles
 * @requires @mui/icons-material
 * @requires date-fns
 * @requires lodash
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Popover from "@mui/material/Popover";
import refreshIcon from "../../assets/refresh.svg";
import { capitalizeFirstLetter } from "../../utils/capitalize";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox as MuiCheckbox,
  Paper,
  styled,
  Button,
  Tooltip,
} from "@mui/material";

import BiosimilarsIcon from "../../assets/Biosimilars.svg";
import PAPIcon from "../../assets/PAP.svg";
import InternationalSourcingIcon from "../../assets/IntSourcing.svg";
import CopayAssistIcon from "../../assets/CoPayAssist.svg";
import ECMPIcon from "../../assets/ECMP.svg";
import GLP1Icon from "../../assets/GLP 1.svg";
import WomensHealthIcon from "../../assets/WomensHealth.svg";
import DiabetesIcon from "../../assets/Diabetes.svg";
import WeightManagementIcon from "../../assets/WeightManagement.svg";
import ScienceIcon from "@mui/icons-material/Science";

import { useLoading } from "../../context/LoadingContext";
import {
  fetchOpportunities,
  updatePapSavings,
  fetchMemberClinicalPrograms,
} from "../../api/oppurtunity";
import { checkIsFormless } from "../../api/assessment";
import { iconColors } from "../../utils/statusColors";
import phoneIcon from "../../assets/Phone.svg";
import formatDateTimeUtil from "../../utils/formatDateTime";
import { formatPhoneNumber } from "../../utils/formatUtils";
import MedicationHistory from "../medication/medicationHistory";
import OutreachHistoryPanel from "./OutreachHistoryPanel";
import ClinicalAssessmentHistoryPanel from "./ClinicalAssessmentHistoryPanel";
import NotesCard from "../membersOutreach/NotesCard";
import EscalationsCard from "../membersOutreach/EscalationsCard";
import OutreachHistoryModal from "../modals/OutreachHistoryModal";
import InternationalEnrollmentModal from "../modals/InternationalEnrollment/InternationalEnrollmentModal";
import ScriptCoEnrollmentModal from "../modals/ScriptCoEnrollment/ScriptCoEnrollmentModal";
import AscellaEnrollmentModal from "../modals/AscellaEntollment/AscellaEnrollmentModal";
import TrackEnrollmentModal from "../modals/TrackEnrollmentModal";
import PapSavingsModal from "../modals/PapSavingsModal";
import BiosimilarsOutcomeModal from "../modals/BiosimilarsOutcomeModal";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  getGrmDetails,
  searchGrmProducts,
  getGrmOrderDetails,
} from "../../api/grm";
import {
  getScriptCoDetails,
  searchScriptCoProducts,
  getScriptCoOrderDetails,
} from "../../api/scriptco";
import CommonDialog from "../common/CommonDialog";
import {
  trackAscellaOrder,
  prepareAscellaReferral,
  getSubmittedReferral,
} from "../../api/ascella";
import { getUserPrimaryRole, isUserL9Role } from "../../utils/role";
import RnVisitSchedulerModal from "../modals/RnVisitSchedulerModal";
import ClinicalProgramsTable from "./ClinicalProgramsTable";
import { usePermissions } from "../../redux/permissions";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

const OPPORTUNITY_HEADERS = [
  { label: "Latest Outcome", width: "20%" },
  { label: "Program", width: "22%" },
  { label: "Member Cost", width: "13%" },
  { label: "Provider", width: "15%" },
  { label: "Pharmacy", width: "15%" },
  { label: "Action", width: "15%" },
];

const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim() !== "") return value;
  }
  return "";
};

const getRecentActivityColor = (activity) =>
  firstNonEmpty(activity?.statusColor, activity?.color) || "#6c757d";

const checkIsBiosimilar = (prog) => {
  if (!prog) return false;
  if (typeof prog === "string")
    return prog.toUpperCase().includes("BIOSIMILAR");
  const name = String(
    prog.name ||
      prog.programName ||
      prog.programDisplayName ||
      prog.displayName ||
      prog.groupName ||
      prog.category ||
      "",
  ).toUpperCase();
  const id = String(prog.id || prog.programId || "").toUpperCase();
  return name.includes("BIOSIMILAR") || id.includes("BIOSIMILAR");
};

/**
 * Generates an SVG filter for recoloring icons
 * @param {string} color - Hex color code
 * @returns {string} SVG filter URL
 */
const getSvgFilter = (color) => {
  const hexToRgb = (hex) => {
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  };

  const [r, g, b] = hexToRgb(color);
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='recolor' color-interpolation-filters='sRGB'%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 ${
    r / 255
  } 0 0 0 0 ${g / 255} 0 0 0 0 ${
    b / 255
  } 0 0 0 1 0'/%3E%3C/filter%3E%3C/svg%3E#recolor")`;
};

/**
 * Returns the appropriate icon for a given category
 * @param {string} category - The category name
 * @param {Object} [style] - Optional style object from API
 * @returns {JSX.Element} Icon component for the category
 */
const getCategoryIcon = (category, status = "") => {
  switch (category) {
    case "Biosimilars":
      return (
        <img
          src={BiosimilarsIcon}
          alt="Biosimilars"
          style={{
            width: "16px",
            height: "16px",
            filter: getSvgFilter(iconColors?.iconColor),
          }}
        />
      );
    case "PAP":
      return (
        <img
          src={PAPIcon}
          alt="PAP"
          style={{
            width: "16px",
            height: "16px",
            filter: getSvgFilter(iconColors?.iconColor),
          }}
        />
      );
    case "International Sourcing":
      return (
        <img
          src={InternationalSourcingIcon}
          alt="International Sourcing"
          style={{
            width: "16px",
            height: "16px",
            filter: getSvgFilter(iconColors?.iconColor),
          }}
        />
      );
    case "Copay Assist":
      return (
        <img
          src={CopayAssistIcon}
          alt="Copay Assist"
          style={{
            width: "16px",
            height: "16px",
            filter: getSvgFilter(iconColors?.iconColor),
          }}
        />
      );
    case "ECMP":
      return (
        <img
          src={ECMPIcon}
          alt="ECMP"
          style={{
            width: "16px",
            height: "16px",
            filter: getSvgFilter(iconColors?.iconColor),
          }}
        />
      );
    case "GLP-1":
      return (
        <img
          src={GLP1Icon}
          alt="GLP-1"
          style={{
            width: "16px",
            height: "16px",
            filter: getSvgFilter(iconColors?.iconColor),
          }}
        />
      );
    case "Women's Health Program":
    case "Women's Health":
      return (
        <img
          src={WomensHealthIcon}
          alt="Women's Health Program"
          style={{
            width: "16px",
            height: "16px",
            filter: getSvgFilter(iconColors?.iconColor),
          }}
        />
      );
    case "Diabetes":
      return (
        <img
          src={DiabetesIcon}
          alt="Diabetes"
          style={{
            width: "16px",
            height: "16px",
            filter: getSvgFilter(iconColors?.iconColor),
          }}
        />
      );
    case "Weight Management":
    case "Weight":
      return (
        <img
          src={WeightManagementIcon}
          alt="Weight Management"
          style={{
            width: "16px",
            height: "16px",
            filter: getSvgFilter(iconColors?.iconColor),
          }}
        />
      );
    default:
      return (
        <ScienceIcon
          style={{
            width: "16px",
            height: "16px",
            color: iconColors?.iconColor,
          }}
        />
      );
  }
};

const isEmptyFirstPage = (response, pageNum) =>
  (!response.content || response.content.length === 0) && pageNum === 0;

const mergeOpportunities = (prev, formatted) => {
  const map = new Map();
  for (const item of [...prev, ...formatted]) {
    map.set(item.opportunityId || item.id, item);
  }
  return Array.from(map.values());
};

const notifyCopayOpportunities = (
  opportunities,
  onCopayOpportunityFound,
  onCopayOpportunitiesFound,
) => {
  const copayOpp = opportunities.find((opp) => opp.category === "Copay Assist");
  onCopayOpportunityFound?.(copayOpp || null);
  const copayOpps = opportunities.filter(
    (opp) => opp.category === "Copay Assist",
  );
  onCopayOpportunitiesFound?.(copayOpps);
};

const formatTimePart = (date) => {
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const upper = timePart.toUpperCase();
  if (upper.includes("AM") || upper.includes("PM")) return timePart;
  const amPm = date.getHours() < 12 ? "AM" : "PM";
  return `${timePart} ${amPm}`;
};

/**
 * Formats a date string to MM/DD/YYYY format with optional time
 * @param {string} dateString - The date string to format
 * @param {string} [time="false"] - Whether to include time in the output
 * @returns {string} Formatted date string
 */
export const formatDateTime = (dateString, time = false) => {
  if (!dateString) return "";

  if (/^\d{2}-\d{2}-\d{2}$/.test(dateString)) {
    const [day, month, year] = dateString.split("-");

    const fullYear =
      Number.parseInt(year, 10) < 100
        ? 2000 + Number.parseInt(year, 10)
        : Number.parseInt(year, 10);
    return `${month}/${day}/${fullYear}`;
  }
  const dateStringWithZ =
    typeof dateString === "string" && !dateString.endsWith("Z")
      ? `${dateString}Z`
      : dateString;

  const date = new Date(dateStringWithZ);
  if (!Number.isNaN(date.getTime())) {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    const timeString = time ? ` ${formatTimePart(date)}` : "";

    return `${month}/${day}/${year}${timeString}`.trim();
  }

  return dateString;
};

/**
 * Custom styled checkbox component
 * @constant {Object} CustomCheckbox
 */
export const CustomCheckbox = styled(MuiCheckbox)({
  padding: 4,
  "&:hover": {
    backgroundColor: "transparent",
  },
  "&.Mui-checked": {
    color: "#403C6F",
    "& .MuiSvgIcon-root": {
      fill: "#403C6F",
      borderColor: "#403C6F",
      "& .MuiSvgIcon-root": {
        fill: "white",
      },
    },
  },
  "&.Mui-disabled": {
    "& .MuiSvgIcon-root": {
      backgroundColor: "transparent",
      borderColor: "rgba(0, 0, 0, 0.26)",
    },
    "&.Mui-checked": {
      "& .MuiSvgIcon-root": {
        backgroundColor: "rgba(0, 0, 0, 0.12)",
      },
    },
  },
});

/**
 * Configuration for the selection checkbox column
 * @constant {Object} checkboxColumn
 */
export const checkboxColumn = {
  id: "selection",
  header: ({ table }) => {
    const items = table.getRowModel().items;
    const allRowsSelected =
      items.length > 0 && items.every((item) => item.getIsSelected());

    return (
      <MuiCheckbox
        checked={allRowsSelected}
        onChange={table.getToggleAllPageRowsSelectedHandler()}
        indeterminate={false}
        disableRipple
        slotProps={{ input: { "aria-label": "Select all opportunity items" } }}
        sx={{
          marginLeft: "12px",
          padding: "4px",
          color: "rgba(0, 0, 0, 0.26)",
          "&.MuiCheckbox-root": {
            "&.MuiCheckbox-indeterminate": {
              display: "none !important",
            },
          },
          "& .MuiSvgIcon-root": {
            "&.MuiSvgIcon-root": {
              "&.MuiCheckbox-indeterminate": {
                display: "none !important",
              },
            },
          },
          "&.Mui-checked": {
            color: "#403C6F",
            "& .MuiSvgIcon-root": {
              fill: "#403C6F",
            },
          },
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
      />
    );
  },
  cell: ({ item }) => (
    <MuiCheckbox
      checked={item.getIsSelected()}
      onChange={item.getToggleSelectedHandler()}
      onClick={(e) => e.stopPropagation()}
      disabled={item.original.discontinued}
      slotProps={{
        input: {
          "aria-label": `Select opportunity item for ${
            item.original?.drugName || item.original?.medicationName || "item"
          }`,
        },
      }}
      sx={{
        marginLeft: "12px",
        padding: "4px",
        "&.Mui-checked": {
          color: "#403C6F",
          "& .MuiSvgIcon-root": {
            fill: "#403C6F",
          },
        },
        color: "#403C6F",
        "&:hover": {
          backgroundColor: "transparent",
        },
        "&.Mui-disabled": {
          color: "rgba(0, 0, 0, 0.26)",
        },
      }}
    />
  ),
  size: 50,
  enableSorting: false,
};
export /**
 * Formats a date string to a localized date and time string
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date and time string
 */
const DateTime = (dateString) => {
  const date = new Date(dateString);
  return `${date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  })} ${date.toLocaleTimeString("en-US", {
    hour: "numeric",
  })}`;
};

/**
 * Builds a program map from response content
 * @param {Array} content - The response content from the API
 * @returns {Object} The program map
 */
const buildProgramMap = (content) => {
  const progMap = {};
  for (const item of content) {
    if (item.program?.programId && item.program?.programName) {
      progMap[item.program.programId] = item.program.programName;
    }
  }
  return progMap;
};

/**
 * Formats a single opportunity item
 * @param {Object} item - The opportunity item to format
 * @param {number} idx - Index of the item
 * @param {string} systemTimeZone - The system time zone
 * @returns {Object} The formatted opportunity item
 */
const formatOpportunityItem = (
  item,
  idx,
  systemTimeZone,
  isShowPhiEnabled,
) => ({
  ...item,
  id: idx + 1,
  opportunityId: item.opportunityId,
  slo: item.slo,
  category: item.program?.programName || "N/A",
  vendor: item?.vendor,
  drug: item.medication?.drugName
    ? capitalizeFirstLetter(item.medication.drugName.toLowerCase())
    : "Unknown",
  drugId: item.medication?.drugId || "",
  ndc: item.medication?.ndc || item.medication?.drugNdc || "",
  isGraduated: item.isGraduated || false,
  strength: item.program?.strength || "N/A",
  providerDetails: item.provider,
  rejectionCode: item.program?.rejectionCode || "N/A",
  paAction: item.program?.paAction || "N/A",
  monitoringReason:
    item.isGraduated ||
    item.monitoringReason ||
    item.program?.status === "DECLINED"
      ? item.monitoringReason || "N/A"
      : "N/A",
  instructions: `Quantity: ${
    item.program?.metricQty || "N/A"
  } | Days Supply: ${item.program?.daySupply || 0}`,
  cost: item.program?.memberCost
    ? `$${new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(item.program.memberCost)}`
    : "$0.00",
  lastFilled: formatDateTime(item.program?.lastFilled, false) || "N/A",
  programId: item.program?.id || "",
  memberProgramId: item.program?.id || "",
  provider: capitalizeFirstLetter(item.provider?.name) || "Unknown",
  providerContactRaw: item.provider?.contact || "",
  providerPhone:
    formatPhoneNumber(item.provider?.contact, isShowPhiEnabled) || "N/A",
  method: item.recentActivity?.outreachMethod || "",
  methodDisplay: item.recentActivity?.outreachMethodDisplayName || "",
  drugNdc: item.medication?.drugNdc || "N/A",
  dateAdded:
    item.isGraduated ||
    item.program?.dateAdded ||
    item.program?.status === "DECLINED"
      ? formatDateTime(item.program?.dateAdded) || "N/A"
      : "N/A",
  pharmacy: capitalizeFirstLetter(item.pharmacy?.name) || "N/A",
  pharmacyContactRaw: item.pharmacy?.contact || "",
  pharmacyPhone:
    formatPhoneNumber(item.pharmacy?.contact, isShowPhiEnabled) || "N/A",
  recentActivity: {
    status: item.recentActivity?.outcomeDisplayName || "",
    at: item.recentActivity?.timestamp
      ? formatDateTimeUtil(item.recentActivity.timestamp, {
          timeZone: systemTimeZone,
        })
      : "N/A",
    role: item.recentActivity?.recentActivityRole || "",
    note: item.recentActivity?.note || "",
    reason: item.recentActivity?.subCategoryDisplayName || "",
    statusColor: item.recentActivity?.colourCode || "",
  },
  status:
    item.program?.status === "ELIGIBLE"
      ? "Not Enrolled"
      : capitalizeFirstLetter(item.program?.status) || "Unknown",
  metricQty: item.program?.metricQty || "N/A",
  daySupply: item.program?.daySupply || "",
  isSubmitted:
    item.isSubmitted === undefined
      ? item.program?.programName !== "Copay Assist"
      : item.isSubmitted,
  vendorReferenceId: item.program?.vendorReferenceId || "",
  planSavings: firstDefinedOrNull(item.program?.planSavings, item.planSavings),
  memberSavings: firstDefinedOrNull(
    item.program?.memberSavings,
    item.memberSavings,
  ),
});

const firstDefinedOrNull = (primary, fallback) => {
  if (primary !== undefined) return primary;
  if (fallback !== undefined) return fallback;
  return null;
};

const extractProductsList = (productSearchResult) => {
  if (Array.isArray(productSearchResult)) return productSearchResult;
  if (productSearchResult?.result) return productSearchResult.result;
  if (productSearchResult?.data) return productSearchResult.data;
  return productSearchResult?.productOptionId ? [productSearchResult] : [];
};

const formatCategoryWithVendor = (item) => {
  const vendorName = item.vendor?.vendorName;
  if (!vendorName) return item.category;
  const displayVendor =
    vendorName === "GRM" ? vendorName : capitalizeFirstLetter(vendorName);
  return `${item.category} (${displayVendor})`;
};

const mapGrmContactMethod = (method) => {
  const value = String(method || "").toLowerCase();
  switch (value) {
    case "mobilephone":
    case "sms":
      return "Mobile Phone";
    case "email":
      return "Email";
    case "alternatephone":
    case "homephone":
      return "Alternate Phone";
    case "alternateemail":
      return "Alternate Email";
    default:
      return "";
  }
};

const csvToArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const mergeOpportunityWithGrmDetails = (
  item,
  fallbackMember,
  grmDetailsResponse,
) => {
  const grmDetails =
    grmDetailsResponse?.result?.[0] || grmDetailsResponse || {};
  const customerDetails = grmDetails?.customerDetails || {};
  const billingAddress = customerDetails.billingAddress || {};
  const deliveryAddress = customerDetails.deliveryAddress || {};
  let memberAddressSource = fallbackMember?.address || {};
  if (Object.keys(billingAddress).length > 0) {
    memberAddressSource = billingAddress;
  } else if (Object.keys(deliveryAddress).length > 0) {
    memberAddressSource = deliveryAddress;
  }

  const baseMember = fallbackMember ? { ...fallbackMember } : {};
  const baseAddress = fallbackMember?.address
    ? { ...fallbackMember.address }
    : {};

  const mergedMember = {
    ...baseMember,
    id:
      fallbackMember?.memberId ||
      fallbackMember?.id ||
      item?.member?.memberId ||
      item?.member?.id ||
      (item?.memberId !== item?.id && item?.memberId !== item?.opportunityId
        ? item?.memberId
        : "") ||
      "",
    memberId:
      fallbackMember?.memberId ||
      fallbackMember?.id ||
      item?.member?.memberId ||
      item?.member?.id ||
      (item?.memberId !== item?.id && item?.memberId !== item?.opportunityId
        ? item?.memberId
        : "") ||
      "",
    firstName:
      customerDetails.firstName ||
      fallbackMember?.firstName ||
      fallbackMember?.name?.split(" ")?.[0] ||
      "",
    lastName:
      customerDetails.lastName ||
      fallbackMember?.lastName ||
      (fallbackMember?.name || "").split(" ").slice(1).join(" "),
    fullName:
      `${
        customerDetails.firstName || fallbackMember?.firstName || ""
      } ${customerDetails.lastName || fallbackMember?.lastName || ""}`.trim() ||
      fallbackMember?.fullName ||
      fallbackMember?.name ||
      "",
    phoneNumber:
      grmDetails?.mobilePhone ||
      fallbackMember?.phoneNumber ||
      fallbackMember?.phone ||
      "",
    mobilePhone:
      grmDetails?.mobilePhone ||
      fallbackMember?.mobilePhone ||
      fallbackMember?.phoneNumber ||
      "",
    alternatePhoneNum:
      grmDetails?.homePhone ||
      fallbackMember?.alternatePhoneNum ||
      fallbackMember?.alternatePhoneNumber ||
      fallbackMember?.alternatePhone ||
      "",
    alternatePhoneNumber:
      grmDetails?.homePhone ||
      fallbackMember?.alternatePhoneNumber ||
      fallbackMember?.alternatePhoneNum ||
      fallbackMember?.alternatePhone ||
      "",
    email:
      grmDetails?.email ||
      fallbackMember?.email ||
      fallbackMember?.contact?.email ||
      "",
    dateOfBirth:
      customerDetails.dateOfBirth ||
      fallbackMember?.dateOfBirth ||
      fallbackMember?.dob ||
      "",
    dob:
      customerDetails.dateOfBirth ||
      fallbackMember?.dob ||
      fallbackMember?.dateOfBirth ||
      "",
    sex:
      customerDetails.sex ||
      fallbackMember?.sex ||
      fallbackMember?.gender ||
      "",
    gender:
      customerDetails.sex ||
      fallbackMember?.gender ||
      fallbackMember?.sex ||
      "",
    allergy:
      customerDetails.drugAllergies ||
      fallbackMember?.allergy ||
      fallbackMember?.allergies ||
      "",
    allergies:
      customerDetails.drugAllergies ||
      fallbackMember?.allergies ||
      fallbackMember?.allergy ||
      "",
    address: {
      ...baseAddress,
      line1: memberAddressSource.line1 || fallbackMember?.address?.line1 || "",
      line2: memberAddressSource.line2 || fallbackMember?.address?.line2 || "",
      city: memberAddressSource.city || fallbackMember?.address?.city || "",
      state: memberAddressSource.state || fallbackMember?.address?.state || "",
      country:
        memberAddressSource.country || fallbackMember?.address?.country || "",
      postalCode:
        memberAddressSource.zipCode ||
        memberAddressSource.postalCode ||
        fallbackMember?.address?.postalCode ||
        fallbackMember?.address?.zip ||
        "",
      zip:
        memberAddressSource.zipCode ||
        memberAddressSource.postalCode ||
        fallbackMember?.address?.zip ||
        fallbackMember?.address?.postalCode ||
        "",
    },
  };

  return {
    ...item,
    memberId:
      fallbackMember?.memberId ||
      fallbackMember?.id ||
      item?.member?.memberId ||
      item?.member?.id ||
      (item?.memberId !== item?.id && item?.memberId !== item?.opportunityId
        ? item?.memberId
        : "") ||
      "",
    member: mergedMember,
    grmDetails,
    billingAddress,
    deliveryAddress,
    preferredCommunicationMethod:
      mapGrmContactMethod(grmDetails?.preferredCommunicationMethod) ||
      item?.preferredCommunicationMethod ||
      "",
    preferredCommunicationMethodForAutoRefills:
      mapGrmContactMethod(
        grmDetails?.preferredCommunicationMethodForAutoRefills,
      ) ||
      item?.preferredCommunicationMethodForAutoRefills ||
      "",
    grmMedical: {
      heightInCm: customerDetails.heightInCm || "",
      weightInKg: customerDetails.weightInKg || "",
      isPregnant: customerDetails.isPregnant,
      pregnancyDueDate: customerDetails.pregnancyDueDate || "",
      isBreastfeeding: customerDetails.isBreastfeeding,
      isSmoker: customerDetails.isSmoker,
      currentMedicalConditions: csvToArray(
        customerDetails.currentMedicalConditions,
      ),
      currentMedicationsAndSupplements: csvToArray(
        customerDetails.currentMedicationsAndSupplements,
      ),
      drugAllergies: customerDetails.drugAllergies || "",
      sex: customerDetails.sex || "",
    },
  };
};

/**
 * Builds a program category map from formatted opportunities
 * @param {Array} formattedOpportunities - The formatted opportunities
 * @param {boolean} fromMemberMonitoring - Flag for member monitoring context
 * @returns {Object} The program category map
 */
const buildProgramCategoryMap = (
  formattedOpportunities,
  fromMemberMonitoring,
) => {
  const programCategoryMap = {};

  const filteredOpportunities = formattedOpportunities.filter(
    (opp) =>
      opp.isGraduated === false &&
      (!fromMemberMonitoring ||
        (opp.status !== "Eligible" && opp.status !== "Declined")),
  );

  for (const opp of filteredOpportunities) {
    const category = opp.category;
    const drugKey = `${opp.drug}-${opp.drugId}-${opp.programId}-${
      opp.ndc || ""
    }`;
    const reason = opp.monitoringReason;

    if (!programCategoryMap[category]) {
      programCategoryMap[category] = {
        drugs: [],
        monitoringReasons: [],
        drugStatusMap: {},
      };
    }

    if (!programCategoryMap[category].drugs.includes(drugKey)) {
      programCategoryMap[category].drugs.push(drugKey);
    }

    programCategoryMap[category].drugStatusMap[drugKey] = opp.status;

    if (
      reason &&
      !programCategoryMap[category].monitoringReasons.includes(reason)
    ) {
      programCategoryMap[category].monitoringReasons.push(reason);
    }
  }
  return programCategoryMap;
};

const buildL9Tabs = (config) => {
  const {
    clinicalTab,
    costSavingsTab,
    clinicalAssessmentTab,
    outreachTab,
    claimsTab,
    notesTab,
    escalationsTab,
    hasNonCgOpportunities,
    hasEscalations,
  } = config;

  const tabs = [clinicalTab];
  if (hasNonCgOpportunities) {
    tabs.push(costSavingsTab);
  }
  tabs.push(clinicalAssessmentTab, outreachTab, claimsTab, notesTab);
  if (hasEscalations) {
    tabs.push(escalationsTab);
  }
  return tabs;
};

const buildStandardTabs = (config) => {
  const {
    costSavingsTab,
    clinicalTab,
    outreachTab,
    claimsTab,
    notesTab,
    escalationsTab,
    shouldShowCostSavings,
    shouldShowClinical,
    hasEscalations,
  } = config;

  const tabs = [];
  if (shouldShowCostSavings) {
    tabs.push(costSavingsTab);
  }
  if (shouldShowClinical) {
    tabs.push(clinicalTab);
  }
  tabs.push(outreachTab, claimsTab, notesTab);
  if (hasEscalations) {
    tabs.push(escalationsTab);
  }
  return tabs;
};

/**
 * Main Opportunities Table Component
 *
 *
 * A comprehensive table for displaying and managing medication opportunities.
 * Supports sorting, filtering, and various actions like editing and discontinuing medications.
 *
 *
 * @param {Object} props - Component props
 * @param {boolean} props.showMonitoringReason - Whether to show monitoring reason column
 * @param {Function} props.setDispositionOpportunity - Callback for selected opportunities
 * @param {string} props.memberId - ID of the member
 * @param {Function} props.setProgramOptions - Callback to set program options
 * @param {Function} props.setProgramDrugMap - Callback to set program-drug mapping
 * @returns {JSX.Element} The rendered opportunities table
 */
const OpportunitiesTable = ({
  showMonitoringReason,
  setDispositionOpportunity,
  memberId,
  setProgramOptions,
  setProgramDrugMap,
  fromMemberMonitoring,
  canWrite = true,
  pageSize = 30,
  onOpenDisposition,
  setProgramMap,
  onLoadingChange,
  isShowPhiEnabled,
  canDelete = true,
  onMedicationLoadingChange,
  onOutreachLoadingChange,
  notes = [],
  selectedNoteType = [],
  searchQuery = "",
  onNoteTypeChange,
  onSearchNotes,
  onToggleNotePin,
  onEditNote,
  setIsNoteModalOpen,
  setIsEscalationModalOpen,
  escalations = [],
  onSelectEscalation,
  showHeaderActions = true,
  externalOutcomeTriggerKey = 0,
  activateEscalationsTabKey = 0,
  onTabChange,
  memberData,
  onGetCopayCard,
  onCopayOpportunityFound,
  onCopayOpportunitiesFound,
  onTotalOpportunitiesChange,
  onOpenClinicalHistory,
  onReopenClinicalHistory,
  onStartClinicalNote,
  clinicalNotePrograms = [],
  programMap = {},
  referenceData = null,
  onScheduleSuccess,
  onMemberDetailsRefresh,
  onOpenBiosimilarsOutcome,
}) => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showLoading, hideLoading } = useLoading();
  const { canRead: cgPlusPermission } = usePermissions(
    "CG+ Assessment Templates",
  );
  const { canRead: memberOutreachPermission } =
    usePermissions("Member Outreach");
  const userRole = String(getUserPrimaryRole() || "").toUpperCase();
  const isL9 = userRole === "L9" || isUserL9Role();
  const isCostSavingsRole = isL9 || ["L6", "L7", "L8", "L9"].includes(userRole);
  const hasCgPlusAccess =
    cgPlusPermission ||
    memberOutreachPermission ||
    ["L6", "L7", "L7B", "L8", "L9", "RN"].includes(userRole);
  const defaultTab =
    isL9 && hasCgPlusAccess ? "clinical_programs" : "opportunities";
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [outreachCount, setOutreachCount] = useState(0);
  const [medicationCount, setMedicationCount] = useState(0);
  const [clinicalAssessmentCount, setClinicalAssessmentCount] = useState(0);

  const [isRnSchedulerModalOpen, setIsRnSchedulerModalOpen] = useState(false);
  const [isAppointmentScheduledPopupOpen, setIsAppointmentScheduledPopupOpen] =
    useState(false);
  const [isLocalBiosimilarsModalOpen, setIsLocalBiosimilarsModalOpen] =
    useState(false);

  const handleOpenBiosimilarsOutcome = useCallback(
    (prog) => {
      if (typeof onOpenBiosimilarsOutcome === "function") {
        onOpenBiosimilarsOutcome(prog);
      } else {
        setIsLocalBiosimilarsModalOpen(true);
      }
    },
    [onOpenBiosimilarsOutcome],
  );
  const [scheduledAppointmentInfo, setScheduledAppointmentInfo] =
    useState(null);

  const [clinicalPrograms, setClinicalPrograms] = useState([]);

  const loadClinicalPrograms = useCallback(
    async (manageLoading = true) => {
      if (!memberId) return;
      try {
        if (manageLoading) showLoading();
        const data = await fetchMemberClinicalPrograms(memberId);
        if (data && Array.isArray(data)) {
          setClinicalPrograms(data);
        } else if (data && Array.isArray(data?.content)) {
          setClinicalPrograms(data.content);
        } else if (data && Array.isArray(data?.programs)) {
          setClinicalPrograms(data.programs);
        } else if (data && Array.isArray(data?.data)) {
          setClinicalPrograms(data.data);
        } else {
          setClinicalPrograms([]);
        }
      } catch (err) {
        console.error("Failed to load clinical programs:", err);
        setClinicalPrograms([]);
      } finally {
        if (manageLoading) hideLoading();
      }
    },
    [memberId, showLoading, hideLoading],
  );

  const handleScheduleSuccess = async (appointmentDetails) => {
    try {
      const stored = JSON.parse(
        localStorage.getItem("scheduled_rn_visits") || "[]",
      );
      stored.push(appointmentDetails);
      localStorage.setItem("scheduled_rn_visits", JSON.stringify(stored));
    } catch (e) {
      console.error("Failed to save scheduled appointment to localStorage:", e);
    }

    setIsRnSchedulerModalOpen(false);
    setScheduledAppointmentInfo(appointmentDetails);
    setIsAppointmentScheduledPopupOpen(true);

    await loadClinicalPrograms();

    if (onScheduleSuccess) {
      await onScheduleSuccess();
    }
  };

  const memberCgPlusPrograms = React.useMemo(() => {
    return (clinicalNotePrograms || []).filter((prog) => {
      const gType = String(prog.groupType || prog.type || "").toUpperCase();
      const normId = String(prog.programId || prog.id || "").toUpperCase();
      return (
        gType !== "GENERAL" &&
        gType !== "OTHER" &&
        normId !== "GENERAL" &&
        normId !== "OTHER" &&
        !checkIsFormless(prog, referenceData)
      );
    });
  }, [clinicalNotePrograms, referenceData]);

  const onlyBiosimilars = React.useMemo(() => {
    let progs = clinicalPrograms;
    if (!progs || progs.length === 0) {
      if (
        programMap &&
        typeof programMap === "object" &&
        Object.keys(programMap).length > 0
      ) {
        progs = Object.entries(programMap).map(([id, name]) => ({
          id,
          name,
          programId: id,
          programName: name,
        }));
      }
    }
    const activeProgs = (progs || []).filter((p) => {
      const gType = String(p.groupType || p.type || "").toUpperCase();
      const progId = String(p.programId || p.id || "").toUpperCase();
      return (
        gType !== "GENERAL" &&
        gType !== "OTHER" &&
        progId !== "GENERAL" &&
        progId !== "OTHER"
      );
    });

    if (activeProgs.length === 0) return false;

    return activeProgs.every((p) => checkIsBiosimilar(p));
  }, [clinicalPrograms, programMap]);
  const getNextScheduledAssessment = useCallback(
    (programName) => {
      try {
        const stored = JSON.parse(
          localStorage.getItem("scheduled_rn_visits") || "[]",
        );
        const memberVisits = stored.filter(
          (v) =>
            String(v.memberId) === String(memberId) &&
            String(v.program || "").toLowerCase() ===
              String(programName).toLowerCase(),
        );
        if (memberVisits.length === 0) return null;

        const now = Date.now();
        const futureVisits = memberVisits
          .map((v) => {
            const cleanTime = (v.time || "00:00:00")
              .replace(/[AP]M/gi, "")
              .trim();
            const isPM = (v.time || "").toUpperCase().includes("PM");
            let [hours, minutes] = cleanTime.split(":");
            if (isPM && Number.parseInt(hours) < 12)
              hours = String(Number.parseInt(hours) + 12);
            if (!isPM && Number.parseInt(hours) === 12) hours = "00";

            const dateStr = `${v.year}-${String(v.month + 1).padStart(2, "0")}-${String(v.day).padStart(2, "0")}T${String(hours).padStart(2, "0")}:${String(minutes || "00").padStart(2, "0")}:00`;
            const timeMs = new Date(dateStr).getTime();
            return { ...v, timeMs };
          })
          .filter((v) => v.timeMs >= now);

        if (futureVisits.length === 0) return null;

        futureVisits.sort((a, b) => a.timeMs - b.timeMs);
        return futureVisits[0];
      } catch (e) {
        console.error("Error finding next scheduled assessment", e);
        return null;
      }
    },
    [memberId],
  );

  const memberCgProgramsList = React.useMemo(() => {
    return clinicalPrograms.map((prog) => {
      const priority = prog.priorityLevel || "";
      const status = prog.outcome || prog.status || "";
      const outcome = prog.status || "";
      const enrollmentDate = prog.enrollmentDate || "N/A";
      const nextAssessmentType = prog.nextAssessmentType || "";
      const nextScheduledStartTime = prog.nextScheduledStartTime || "";
      const programName =
        prog.programName ||
        prog.groupName ||
        prog.name ||
        prog.programDisplayName ||
        "";

      return {
        id: prog.programId || prog.id,
        name: programName,
        status: status,
        priorityLevel: priority,
        assignedRn: prog.assignedRnName || "",
        enrollmentDate: enrollmentDate,
        currentAssessmentType: prog.assessmentType
          ? prog.assessmentType.replaceAll("_", " ").toLowerCase()
          : "Initial Assessment",
        lastAssessmentDate: prog.completionTime || "N/A",
        outcome: outcome,
        outcomeTimestamp: prog.completionTime || "",
        nextAssessmentType: nextAssessmentType,
        nextScheduledStartTime: nextScheduledStartTime,
        outcomeColour:
          prog.outcomeColour ||
          prog.outcomeColor ||
          prog.outcome_colour ||
          prog.outcome_color ||
          prog.outcomeColorCode ||
          "",
        subReason:
          prog.subReason ||
          prog.sub_reason ||
          prog.outcomeReason ||
          prog.outcome_reason ||
          "",
      };
    });
  }, [clinicalPrograms]);

  const [openAltDropdown, setOpenAltDropdown] = useState(null);
  const [altAnchorEl, setAltAnchorEl] = useState(null);

  const isMounted = React.useRef(true);

  const [page] = useState(0);
  const isFetchingMoreRef = useRef(false);

  const [searchText] = useState("");
  const [totalElements, setTotalElements] = useState(0);
  const lastProcessedOutcomeTriggerRef = useRef(0);

  const visibleTabs = React.useMemo(() => {
    const hasCgPrograms =
      Array.isArray(clinicalPrograms) && clinicalPrograms.length > 0;
    const hasNonCgOpportunities =
      (Array.isArray(opportunities) && opportunities.length > 0) ||
      totalElements > 0;
    const hasEscalations = Array.isArray(escalations) && escalations.length > 0;

    const costSavingsTab = {
      id: "opportunities",
      label: isCostSavingsRole ? "Cost Savings Opportunities" : "Opportunities",
    };
    const clinicalTab = { id: "clinical_programs", label: "Clinical Programs" };
    const clinicalAssessmentTab = {
      id: "clinical_assessment_history",
      label: "Clinical Assessment History",
    };
    const outreachTab = { id: "outreach", label: "Outreach History" };
    const claimsTab = { id: "claims", label: "Claims" };
    const notesTab = { id: "notes", label: "Notes" };
    const escalationsTab = { id: "escalations", label: "Escalations" };

    if (isL9) {
      return buildL9Tabs({
        clinicalTab,
        costSavingsTab,
        clinicalAssessmentTab,
        outreachTab,
        claimsTab,
        notesTab,
        escalationsTab,
        hasNonCgOpportunities,
        hasEscalations,
      });
    }

    const isL6L7 = ["L6", "L7"].includes(userRole);
    const shouldShowCostSavings = !isL6L7 || loading || hasNonCgOpportunities;
    const shouldShowClinical = hasCgPrograms && hasCgPlusAccess;

    return buildStandardTabs({
      costSavingsTab,
      clinicalTab,
      outreachTab,
      claimsTab,
      notesTab,
      escalationsTab,
      shouldShowCostSavings,
      shouldShowClinical,
      hasEscalations,
    });
  }, [
    isL9,
    isCostSavingsRole,
    userRole,
    loading,
    hasCgPlusAccess,
    clinicalPrograms,
    escalations,
    opportunities,
    totalElements,
  ]);

  useEffect(() => {
    if (
      visibleTabs.length > 0 &&
      !visibleTabs.some((tab) => tab.id === activeTab)
    ) {
      const nextTab = visibleTabs[0].id;
      setActiveTab(nextTab);
      onTabChange?.(nextTab);
      onOutreachLoadingChange?.(nextTab === "outreach");
    }
  }, [visibleTabs, activeTab, onTabChange, onOutreachLoadingChange]);

  // Incrementing activateEscalationsTabKey (after a new escalation is
  // created - see MemberDetailsPage's handleSaveEscalation) jumps here to
  // the Escalations tab. escalations is refreshed just before the increment,
  // so by the time this fires the tab is already in visibleTabs.
  useEffect(() => {
    if (activateEscalationsTabKey > 0) {
      setActiveTab("escalations");
      onTabChange?.("escalations");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activateEscalationsTabKey]);

  const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isInternationalEnrollmentOpen, setIsInternationalEnrollmentOpen] =
    useState(false);
  const [isScriptCoEnrollmentOpen, setIsScriptCoEnrollmentOpen] =
    useState(false);
  const [isAscellaEnrollmentOpen, setIsAscellaEnrollmentOpen] = useState(false);
  const [isTrackEnrollmentOpen, setIsTrackEnrollmentOpen] = useState(false);
  const [trackOrdersData, setTrackOrdersData] = useState([]);
  const [isTrackAscella, setIsTrackAscella] = useState(false);
  const [selectedInternationalOpp, setSelectedInternationalOpp] =
    useState(null);
  const [selectedScriptCoOpp, setSelectedScriptCoOpp] = useState(null);
  const [selectedAscellaOpp, setSelectedAscellaOpp] = useState(null);
  const [submittedAscellaOpps, setSubmittedAscellaOpps] = useState([]);
  const [selectedRows] = useState([]);
  const [grmErrorMsg, setGrmErrorMsg] = useState("");
  const [isPapSavingsOpen, setIsPapSavingsOpen] = useState(false);
  const [selectedPapOpp, setSelectedPapOpp] = useState(null);

  console.log(memberData, "memberData in oppurtunities table");

  const openAscellaEnrollment = useCallback(
    async (item) => {
      const fallbackMember = memberData || {};
      const memberProgramId =
        item?.memberProgramId || item?.opportunityId || item?.id;

      let opportunityToOpen = {
        ...item,
        member: fallbackMember,
      };

      const referralId =
        item?.vendorReferenceId ||
        item?.ascellaDetails?.vendorReferenceId ||
        item?.orderId;
      const cleanReferralId = referralId
        ? String(referralId).replace("#", "").trim()
        : "";
      const isSubmitted = !!(
        item?.isSubmitted ||
        item?.vendorReferenceId ||
        submittedAscellaOpps.includes(item?.opportunityId) ||
        submittedAscellaOpps.includes(item?.memberProgramId)
      );

      try {
        showLoading();
        let ascellaDetails;
        if (isSubmitted && cleanReferralId) {
          ascellaDetails = await getSubmittedReferral(memberProgramId);
        } else {
          ascellaDetails = await prepareAscellaReferral(memberProgramId);
        }
        opportunityToOpen = {
          ...opportunityToOpen,
          ascellaDetails,
        };
      } catch (error) {
        console.error("Failed to load Ascella details for Ascella:", error);
      } finally {
        hideLoading();
      }

      setSelectedAscellaOpp(opportunityToOpen);
      setIsAscellaEnrollmentOpen(true);
    },
    [memberData, showLoading, hideLoading, submittedAscellaOpps],
  );

  const openInternationalEnrollment = useCallback(
    async (item) => {
      const fallbackMember = memberData || {};
      const groupCode =
        item?.member?.groupNum ||
        item?.member?.groupId ||
        item?.groupNum ||
        item?.groupId ||
        item?.member?.groupCode ||
        item?.groupCode ||
        fallbackMember?.groupNum ||
        fallbackMember?.groupId ||
        fallbackMember?.groupCode ||
        "";

      const hasVendorRefId =
        item.vendorReferenceId ||
        item.vendorPreferenceId ||
        item.program?.vendorReferenceId;
      const ndcValue =
        item.ndc || item.drugNdc || item.drug_ndc || item.ndcCode || "";
      const productSearchPayload = {
        searchTerm: ndcValue,
        groupCode,
      };

      try {
        showLoading();
        let productOptionId = null;

        if (!hasVendorRefId) {
          const productSearchResult =
            await searchGrmProducts(productSearchPayload);
          if (
            !productSearchResult ||
            productSearchResult.success === false ||
            !productSearchResult.result ||
            productSearchResult.result.length === 0
          ) {
            setGrmErrorMsg(
              "The member's current medication was not found in GRM's enrollment drug list. Please review other available enrollment opportunities for this member.",
            );
            return;
          }

          if (productSearchResult?.result?.length > 0) {
            productOptionId = productSearchResult.result[0].productOptionId;
          }
        }

        const grmPayload = {
          firstName: fallbackMember?.firstName || "",
          lastName: fallbackMember?.lastName || "",
          dateOfBirth: fallbackMember?.dateOfBirth || fallbackMember?.dob || "",
          subscriberId: fallbackMember?.personCode
            ? `${fallbackMember?.subscriberNum || ""}-${fallbackMember?.personCode}`
            : fallbackMember?.subscriberNum || "",
          groupCode,
        };

        showLoading();
        const grmDetails = await getGrmDetails(grmPayload);
        let opportunityToOpen = mergeOpportunityWithGrmDetails(
          item,
          fallbackMember,
          grmDetails,
        );
        opportunityToOpen.grmProductOptionId = productOptionId;

        if (item.vendorReferenceId) {
          try {
            const cleanOrderId = String(item.vendorReferenceId)
              .replaceAll("#", "")
              .replaceAll(/GRM/gi, "")
              .trim();
            const orderHistory = await getGrmOrderDetails(cleanOrderId);
            if (orderHistory) {
              opportunityToOpen.grmOrderDetails = orderHistory;
            }
          } catch (err) {
            console.error("Failed to fetch GRM order history details:", err);
          }
        }

        setSelectedInternationalOpp(opportunityToOpen);
        setIsInternationalEnrollmentOpen(true);
      } catch (error) {
        console.error("Failed to load GRM details:", error);
      } finally {
        hideLoading();
      }
    },
    [hideLoading, memberData, showLoading],
  );

  const openScriptCoEnrollment = useCallback(
    async (item) => {
      const fallbackMember = memberData || {};
      const hasVendorRefId =
        item.vendorReferenceId ||
        item.vendorPreferenceId ||
        item.program?.vendorReferenceId;
      const ndcValue =
        item.ndc || item.drugNdc || item.drug_ndc || item.ndcCode || "";
      const productSearchPayload = {
        searchTerm: ndcValue,
      };

      try {
        showLoading();
        let productOptionId = null;
        let productSearchResultData = null;

        if (!hasVendorRefId) {
          const productSearchResult =
            await searchScriptCoProducts(productSearchPayload);

          productSearchResultData = productSearchResult;
          const productsList = extractProductsList(productSearchResult);

          if (!productsList || productsList.length === 0) {
            setGrmErrorMsg(
              "The member's current medication was not found in ScriptCo's enrollment drug list. Please review other available enrollment opportunities for this member.",
            );
            return;
          }

          productOptionId =
            productsList[0]?.productOptionId ||
            productsList[0]?.id ||
            productsList[0]?.productId;
        }

        const patientGuid =
          fallbackMember?.scriptCoPatientGuid ||
          fallbackMember?.scriptcoPatientGuid ||
          fallbackMember?.patientGUID ||
          item?.scriptCoPatientGuid ||
          item?.scriptcoPatientGuid ||
          item?.patientGUID ||
          item?.member?.scriptCoPatientGuid ||
          item?.member?.scriptcoPatientGuid ||
          item?.member?.patientGUID ||
          "";

        showLoading();
        const scriptCoDetails = patientGuid
          ? await getScriptCoDetails(patientGuid)
          : null;
        let opportunityToOpen = mergeOpportunityWithGrmDetails(
          item,
          fallbackMember,
          scriptCoDetails,
        );
        opportunityToOpen.scriptCoProductOptionId = productOptionId;
        opportunityToOpen.scriptCoSearchResult = productSearchResultData;
        opportunityToOpen.scriptCoDetails =
          scriptCoDetails?.data?.[0] ||
          scriptCoDetails?.data ||
          scriptCoDetails?.result?.[0] ||
          scriptCoDetails;

        if (item.vendorReferenceId) {
          try {
            const cleanOrderId = String(item.vendorReferenceId)
              .replaceAll("#", "")
              .replaceAll(/SCRIPTCO|GRM/gi, "")
              .trim();
            const orderHistory = await getScriptCoOrderDetails(cleanOrderId);
            if (orderHistory) {
              opportunityToOpen.scriptCoOrderDetails = orderHistory;
            }
          } catch (err) {
            console.error(
              "Failed to fetch ScriptCo order history details:",
              err,
            );
          }
        }

        setSelectedScriptCoOpp(opportunityToOpen);
        setIsScriptCoEnrollmentOpen(true);
      } catch (error) {
        console.error("Failed to load ScriptCo details:", error);
      } finally {
        hideLoading();
      }
    },
    [hideLoading, memberData, showLoading],
  );

  const handleAddEditOutcome = useCallback(() => {
    if (!canWrite) return;

    const nonGraduatedCount = opportunities.filter(
      (item) => item.isGraduated === false,
    ).length;

    const selectedData = opportunities
      .filter((item) => selectedRows.includes(item.id))
      .map((item) => ({
        ...item,
        opportunityId: item.opportunityId,
        slo: item.slo,
      }));

    if (typeof setDispositionOpportunity === "function") {
      setDispositionOpportunity({
        data: opportunities,
        count: nonGraduatedCount,
        selected: selectedData,
      });
    }

    if (typeof onOpenDisposition === "function") {
      onOpenDisposition();
    }
  }, [
    canWrite,
    opportunities,
    selectedRows,
    setDispositionOpportunity,
    onOpenDisposition,
  ]);

  useEffect(() => {
    if (!externalOutcomeTriggerKey) return;
    if (externalOutcomeTriggerKey === lastProcessedOutcomeTriggerRef.current)
      return;

    lastProcessedOutcomeTriggerRef.current = externalOutcomeTriggerKey;
    handleAddEditOutcome();
  }, [externalOutcomeTriggerKey, handleAddEditOutcome]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loading);
    }
  }, [loading, onLoadingChange]);

  const beginPageLoading = (pageNum) => {
    if (pageNum > 0) {
      isFetchingMoreRef.current = true;
    } else {
      setLoading(true);
    }
  };

  const endPageLoading = (pageNum) => {
    if (pageNum > 0) {
      isFetchingMoreRef.current = false;
    } else {
      setLoading(false);
    }
  };

  const applyOpportunitiesPage = (formatted, pageNum) => {
    if (pageNum > 0) {
      setOpportunities((prev) => {
        const values = mergeOpportunities(prev, formatted);
        notifyCopayOpportunities(
          values,
          onCopayOpportunityFound,
          onCopayOpportunitiesFound,
        );
        return values;
      });
    } else {
      setOpportunities(formatted);
      notifyCopayOpportunities(
        formatted,
        onCopayOpportunityFound,
        onCopayOpportunitiesFound,
      );
    }
  };

  const loadOpportunities = useCallback(
    async (
      searchQuery = "",
      pageNum = page,
      pageSizeArg = undefined,
      manageLoading = true,
    ) => {
      if (!memberId) return;
      if (manageLoading) showLoading();

      beginPageLoading(pageNum);
      let response;
      try {
        if (manageLoading) showLoading();
        response = await fetchOpportunities(
          memberId,
          searchQuery,
          pageNum,
          pageSizeArg || pageSize,
        );
        const progMap = buildProgramMap(response.content);
        setProgramMap(progMap);

        const totalCount = response.totalElements || 0;
        setTotalElements(totalCount);
        onTotalOpportunitiesChange?.(totalCount);

        if (isEmptyFirstPage(response, pageNum)) {
          setOpportunities([]);
          return;
        }

        const formatted = response.content.map((item, idx) =>
          formatOpportunityItem(item, idx, systemTimeZone, isShowPhiEnabled),
        );

        applyOpportunitiesPage(formatted, pageNum);

        const programCategoryMap = buildProgramCategoryMap(
          formatted,
          fromMemberMonitoring,
        );

        setProgramOptions(Object.keys(programCategoryMap));
        setProgramDrugMap(programCategoryMap);
      } catch (err) {
        if (manageLoading) hideLoading();
        if (pageNum > 0) isFetchingMoreRef.current = false;
        else if (manageLoading) hideLoading();
        return err.message || "Failed to load opportunities";
      } finally {
        if (manageLoading) hideLoading();

        endPageLoading(pageNum);
        if (manageLoading) hideLoading();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      page,
      pageSize,
      memberId,
      setProgramOptions,
      setProgramDrugMap,
      setProgramMap,
      setOpportunities,
      showLoading,
      hideLoading,
      fromMemberMonitoring,
      systemTimeZone,
      isShowPhiEnabled,
    ],
  );

  const handleAscellaSubmissionSuccess = useCallback(
    (oppId) => {
      setSubmittedAscellaOpps((prev) => [...prev, oppId]);
      loadOpportunities();
    },
    [loadOpportunities],
  );

  const handleEditPap = useCallback((item) => {
    setSelectedPapOpp(item);
    setIsPapSavingsOpen(true);
  }, []);

  const handleSavePapSavings = useCallback(
    async (planSavings, memberSavings) => {
      if (!selectedPapOpp || !memberId) return;
      const opportunityId =
        selectedPapOpp.programId ||
        selectedPapOpp.memberProgramId ||
        selectedPapOpp.program?.id ||
        selectedPapOpp.opportunityId ||
        selectedPapOpp.id;
      await updatePapSavings(
        memberId,
        opportunityId,
        planSavings,
        memberSavings,
      );
      loadOpportunities(searchText || "", 0, pageSize);
    },
    [selectedPapOpp, memberId, loadOpportunities, searchText, pageSize],
  );

  useEffect(() => {
    if (!memberId) return;
    const fetchInitialData = async () => {
      showLoading();
      setLoading(true);
      try {
        const promises = [
          loadOpportunities(searchText, page, pageSize, false),
          loadClinicalPrograms(false),
        ];
        await Promise.all(promises);
      } catch (e) {
        console.error("Failed to load opportunities and clinical programs:", e);
      } finally {
        setLoading(false);
        hideLoading();
      }
    };
    void fetchInitialData();

    return () => {
      isMounted.current = false;
    };
  }, [
    memberId,
    searchText,
    page,
    pageSize,
    hasCgPlusAccess,
    loadOpportunities,
    loadClinicalPrograms,
    showLoading,
    hideLoading,
  ]);

  // Recompute formatted phone fields when PHI toggle changes
  useEffect(() => {
    setOpportunities((prev) => {
      if (!prev || prev.length === 0) return prev;
      return prev.map((item) => ({
        ...item,
        providerPhone:
          formatPhoneNumber(item.providerContactRaw || "", isShowPhiEnabled) ||
          "N/A",
        pharmacyPhone:
          formatPhoneNumber(item.pharmacyContactRaw || "", isShowPhiEnabled) ||
          "N/A",
      }));
    });
  }, [isShowPhiEnabled]);

  const groupedByDrug = React.useMemo(() => {
    const groups = {};
    for (const item of opportunities) {
      // Programs like International Sourcing can come back with no
      // medication attached at all - falling back to opportunityId/id (the
      // latter is always set, from formatOpportunityItem) keeps those items
      // visible as their own group instead of silently dropping them, which
      // previously made the whole tab look empty even when opportunities
      // existed.
      const groupKey = item.medication?.drugId || item.opportunityId || item.id;
      if (!groupKey) continue;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          drugName: item.medication?.drugName || item.drug,
          strength: item.medication?.strength,
          metricQty: item.medication?.metricQty,
          programs: [],
        };
      }
      groups[groupKey].programs.push(item);
    }
    return Object.values(groups);
  }, [opportunities]);

  const draftClinicalProgram = React.useMemo(() => {
    const prog = (
      clinicalPrograms.filter((e) => e.assessmentType !== "N/A") || []
    ).find((p) => p.clinicalAssessmentId || p.assessmentId);
    if (!prog) return null;
    return {
      ...prog,
      category: prog.category || prog.programName,
      clinicalAssessmentId: prog.clinicalAssessmentId || prog.assessmentId,
    };
  }, [clinicalPrograms]);

  const clinicalAssessmentId =
    memberData?.clinicalAssessmentId ||
    memberData?.assessmentId ||
    draftClinicalProgram?.clinicalAssessmentId;

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      <OutreachHistoryModal
        open={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        memberId={memberId}
      />
      <InternationalEnrollmentModal
        open={isInternationalEnrollmentOpen}
        onClose={() => setIsInternationalEnrollmentOpen(false)}
        opportunity={selectedInternationalOpp}
        onSuccess={() => loadOpportunities(searchText, 0, pageSize)}
      />
      <ScriptCoEnrollmentModal
        open={isScriptCoEnrollmentOpen}
        onClose={() => setIsScriptCoEnrollmentOpen(false)}
        opportunity={selectedScriptCoOpp}
        onSuccess={() => loadOpportunities(searchText, 0, pageSize)}
        onMemberDetailsRefresh={onMemberDetailsRefresh}
      />
      <AscellaEnrollmentModal
        open={isAscellaEnrollmentOpen}
        onClose={() => setIsAscellaEnrollmentOpen(false)}
        opportunity={selectedAscellaOpp}
        onSubmissionSuccess={handleAscellaSubmissionSuccess}
        memberData={memberData}
      />
      <TrackEnrollmentModal
        open={isTrackEnrollmentOpen}
        onClose={() => setIsTrackEnrollmentOpen(false)}
        orders={trackOrdersData}
        isAscella={isTrackAscella}
      />
      <PapSavingsModal
        open={isPapSavingsOpen}
        onClose={() => setIsPapSavingsOpen(false)}
        opportunity={selectedPapOpp}
        onSave={handleSavePapSavings}
      />
      <CommonDialog
        open={Boolean(grmErrorMsg)}
        onClose={() => setGrmErrorMsg("")}
        variant="alert"
        icon={<ErrorOutlineIcon sx={{ color: "#E53935", fontSize: "32px" }} />}
        title={
          grmErrorMsg?.toLowerCase().includes("scriptco")
            ? "Unable to Submit to Scriptco"
            : "Unable to Submit to GRM"
        }
        actions={
          <Button
            variant="contained"
            onClick={() => setGrmErrorMsg("")}
            sx={{
              borderRadius: "50px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              backgroundColor: "#403C6F",
              color: "#FFF",
              px: 6,
              py: 1,
              boxShadow: "none",
              width: "100%",
              maxWidth: "200px",
              "&:hover": {
                backgroundColor: "#312e5e",
                boxShadow: "none",
              },
            }}
          >
            Close
          </Button>
        }
      >
        {grmErrorMsg}
      </CommonDialog>
      <Paper
        sx={{
          pt: 3,
          px: 3,
          borderRadius: 2,
          border: "1px solid #E8E8E9",
          boxShadow: "none",
          width: "100%",
          boxSizing: "border-box",
          mt: 0,
          mb: 0,
          display: "flex",
          flexDirection: "column",
          maxHeight: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            mb: 1,
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box
            role="tablist"
            aria-label="Member detail tabs"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              p: 0.5,
              borderRadius: "999px",
              backgroundColor: "#ECECF1",
              border: "1px solid #E1E0EA",
              flexWrap: "wrap",
            }}
          >
            {visibleTabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <Button
                  key={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  variant="text"
                  size="small"
                  onClick={() => {
                    setActiveTab(tab.id);
                    onTabChange?.(tab.id);
                    onOutreachLoadingChange?.(tab.id === "outreach");
                  }}
                  disableRipple
                  sx={{
                    color: isActive ? "#2F2A5C" : "#66638C",
                    textTransform: "none",
                    borderRadius: "999px",
                    fontWeight: isActive ? 600 : 500,
                    fontSize: "14px",
                    px: 1.5,
                    py: 0.8,
                    minHeight: "36px",
                    backgroundColor: isActive ? "#FFFFFF" : "transparent",
                    border: isActive
                      ? "1px solid #D7D5E4"
                      : "1px solid transparent",
                    boxShadow: isActive
                      ? "0 1px 2px rgba(34, 24, 84, 0.08)"
                      : "none",
                    "&:hover": {
                      backgroundColor: isActive
                        ? "#FFFFFF"
                        : "rgba(64, 60, 111, 0.08)",
                    },
                  }}
                >
                  {tab.label}
                </Button>
              );
            })}
          </Box>
          {showHeaderActions &&
            [
              "opportunities",
              "notes",
              "outreach",
              "claims",
              "clinical_programs",
              "escalations",
              "clinical_assessment_history",
            ].includes(activeTab) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                {getUserPrimaryRole() === "L9" ? (
                  <>
                    {(Boolean(clinicalAssessmentId) ||
                      (Array.isArray(clinicalPrograms) &&
                        clinicalPrograms.length > 0)) && (
                      <Button
                        variant="contained"
                        size="small"
                        disabled={
                          !canWrite ||
                          Boolean(onlyBiosimilars && !clinicalAssessmentId)
                        }
                        sx={{
                          backgroundColor: "#403C6F",
                          color: "#fff",
                          textTransform: "none",
                          borderRadius: "50px",
                          fontWeight: 600,
                          fontSize: "14px",
                          pt: 0.8,
                          pb: 0.8,
                          px: 1.5,
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor: "#4b3f72",
                            boxShadow: "none",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "#ECECF1",
                            color: "#7F7C9F",
                            cursor: "not-allowed",
                            pointerEvents: "auto",
                          },
                        }}
                        onClick={() => {
                          if (onlyBiosimilars && !clinicalAssessmentId) {
                            return;
                          }
                          if (onStartClinicalNote) {
                            onStartClinicalNote(
                              draftClinicalProgram ||
                                (clinicalAssessmentId
                                  ? { clinicalAssessmentId }
                                  : null),
                              onlyBiosimilars,
                            );
                          }
                        }}
                      >
                        {clinicalAssessmentId
                          ? "Edit Clinical Assessment (Draft)"
                          : "Start Clinical Assessment"}
                      </Button>
                    )}
                    {/* {memberCgPlusPrograms.length > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!canWrite}
                        onClick={() => setIsRnSchedulerModalOpen(true)}
                        sx={{
                          color: "#403C6F",
                          borderColor: "#403C6F",
                          textTransform: "none",
                          border: "1px solid #E8E8E9",
                          borderRadius: 20,
                          fontWeight: 400,
                          fontSize: "14px",
                          pt: 0.8,
                          pb: 0.8,
                          px: 1.5,
                          background: "#ffffff",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            backgroundColor: "rgba(70, 64, 90, 0.04)",
                            borderColor: "#403C6F",
                          },
                          "&.Mui-disabled": {
                            cursor: "not-allowed",
                            color: "#999893",
                            pointerEvents: "auto !important",
                          },
                        }}
                      >
                        Schedule RN Visit
                      </Button>
                    )} */}
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!canWrite}
                      onClick={() => {
                        if (
                          canWrite &&
                          typeof setIsNoteModalOpen === "function"
                        ) {
                          setIsNoteModalOpen(true);
                        }
                      }}
                      sx={{
                        color: "#403C6F",
                        borderColor: "#403C6F",
                        textTransform: "none",
                        border: "1px solid #E8E8E9",
                        borderRadius: 20,
                        fontWeight: 400,
                        fontSize: "14px",
                        pt: 0.8,
                        pb: 0.8,
                        px: 1.5,
                        background: "#ffffff",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          backgroundColor: "rgba(70, 64, 90, 0.04)",
                          borderColor: "#403C6F",
                        },
                        "&.Mui-disabled": {
                          cursor: "not-allowed",
                          color: "#999893",
                          pointerEvents: "auto !important",
                        },
                      }}
                    >
                      Add Note
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!canWrite}
                      onClick={() => {
                        if (
                          canWrite &&
                          typeof setIsEscalationModalOpen === "function"
                        ) {
                          setIsEscalationModalOpen(true);
                        }
                      }}
                      sx={{
                        color: "#403C6F",
                        borderColor: "#403C6F",
                        textTransform: "none",
                        border: "1px solid #E8E8E9",
                        borderRadius: 20,
                        fontWeight: 400,
                        fontSize: "14px",
                        pt: 0.8,
                        pb: 0.8,
                        px: 1.5,
                        background: "#ffffff",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          backgroundColor: "rgba(70, 64, 90, 0.04)",
                          borderColor: "#403C6F",
                        },
                        "&.Mui-disabled": {
                          cursor: "not-allowed",
                          color: "#999893",
                          pointerEvents: "auto !important",
                        },
                      }}
                    >
                      Create Escalation
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!canWrite}
                      sx={{
                        color: "#fff",
                        textTransform: "none",
                        border: "1px solid #E8E8E9",
                        borderRadius: 20,
                        fontWeight: 400,
                        fontSize: "14px",
                        pt: 0.8,
                        pb: 0.8,
                        px: 1.5,
                        background: "#403C6F",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          backgroundColor: "#403C6F !important",
                          borderColor: "#403C6F",
                        },
                        "&.Mui-disabled": {
                          cursor: "not-allowed",
                          color: "#999893",
                          pointerEvents: "auto !important",
                        },
                      }}
                      onClick={handleAddEditOutcome}
                    >
                      Add/Edit Outcome
                    </Button>
                    {/* {memberCgPlusPrograms.length > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!canWrite}
                        onClick={() => setIsRnSchedulerModalOpen(true)}
                        sx={{
                          color: "#403C6F",
                          borderColor: "#403C6F",
                          textTransform: "none",
                          border: "1px solid #E8E8E9",
                          borderRadius: 20,
                          fontWeight: 400,
                          fontSize: "14px",
                          pt: 0.8,
                          pb: 0.8,
                          px: 1.5,
                          background: "#ffffff",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            backgroundColor: "rgba(70, 64, 90, 0.04)",
                            borderColor: "#403C6F",
                          },
                          "&.Mui-disabled": {
                            cursor: "not-allowed",
                            color: "#999893",
                            pointerEvents: "auto !important",
                          },
                        }}
                      >
                        Schedule RN Visit
                      </Button>
                    )} */}
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!canWrite}
                      onClick={() => {
                        if (
                          canWrite &&
                          typeof setIsNoteModalOpen === "function"
                        ) {
                          setIsNoteModalOpen(true);
                        }
                      }}
                      sx={{
                        color: "#403C6F",
                        borderColor: "#403C6F",
                        textTransform: "none",
                        border: "1px solid #E8E8E9",
                        borderRadius: 20,
                        fontWeight: 400,
                        fontSize: "14px",
                        pt: 0.8,
                        pb: 0.8,
                        px: 1.5,
                        background: "#ffffff",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          backgroundColor: "rgba(70, 64, 90, 0.04)",
                          borderColor: "#403C6F",
                        },
                        "&.Mui-disabled": {
                          cursor: "not-allowed",
                          color: "#999893",
                          pointerEvents: "auto !important",
                        },
                      }}
                    >
                      Add Note
                    </Button>
                  </>
                )}
              </Box>
            )}
        </Box>
        <Box
          sx={{
            flex: 1,
            // minHeight: { xs: "60vh", lg: "78vh", xl: "80vh" },
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {[
            "opportunities",
            "outreach",
            "claims",
            "clinical_assessment_history",
          ].includes(activeTab) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                pr: "8px",
                pb: 1,
                mt: 1,
              }}
            >
              <Typography
                sx={{
                  color: "#080A0E8A",
                  fontWeight: 500,
                  fontStyle: "italic",
                  fontSize: "16px",
                  lineHeight: "150%",
                  letterSpacing: 0,
                  userSelect: "none",
                }}
              >
                {(() => {
                  let count = medicationCount;
                  if (activeTab === "opportunities") {
                    count = totalElements;
                  } else if (activeTab === "outreach") {
                    count = outreachCount;
                  } else if (activeTab === "clinical_assessment_history") {
                    count = clinicalAssessmentCount;
                  }
                  return `Total ${count} ${count === 1 ? "Record" : "Records"}`;
                })()}
              </Typography>
            </Box>
          )}

          {activeTab === "opportunities" && (
            <Box
              sx={{
                flex: "0 1 auto",
                backgroundColor: "#ECECF1",
                borderRadius: 2,
                mt: 1,
                overflowY: "auto",
                mb: 2,
                p: { xs: 1, md: 1.5 },
              }}
            >
              {groupedByDrug.length === 0 ? (
                <Box
                  sx={{
                    borderRadius: 2,
                    backgroundColor: "#FFFFFF",
                    overflow: "hidden",
                    border: "1px solid #E8E8E9",
                  }}
                >
                  <TableContainer
                    sx={{
                      borderRadius: "8px",
                      overflowY: "auto",
                      overflowX: "auto",
                      "&::-webkit-scrollbar": {
                        width: "6px",
                        height: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#F5F5F5",
                        borderRadius: "4px",
                        margin: "4px 0",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#A7A5BD",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        background: "#8E8CA8",
                      },
                      scrollbarWidth: "thin",
                      scrollbarColor: "#A7A5BD #F5F5F5",
                    }}
                  >
                    <Table
                      stickyHeader
                      size="small"
                      sx={{ minWidth: "1150px", tableLayout: "fixed" }}
                    >
                      <TableHead>
                        <TableRow
                          sx={{
                            backgroundColor: "#CFCEDA",
                            position: "sticky",
                            top: 0,
                            zIndex: 5000,
                            boxShadow: "0 2px 0 rgba(0,0,0,0.05)",
                          }}
                        >
                          {OPPORTUNITY_HEADERS.map((header) => (
                            <TableCell
                              key={header.label}
                              sx={{
                                backgroundColor: "#CFCEDA",
                                color: "#403C6F",
                                fontWeight: 700,
                                fontSize: "14px",
                                py: 1.5,
                                px: 1.5,
                                borderBottom: "none",
                                whiteSpace: "nowrap",
                                width: header.width,
                                position: "sticky",
                                top: 0,
                                zIndex: 5000,
                                borderColor: "#f5f5f8ff",
                                borderWidth: "0px 1px 0px 1px",
                                borderStyle: "solid",
                              }}
                            >
                              {header.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            colSpan={OPPORTUNITY_HEADERS.length}
                            align="center"
                            sx={{
                              py: 10,
                              color: "#66638C",
                              fontSize: "14px",
                              borderBottom: "none",
                            }}
                          >
                            This member is not currently eligible for any
                            Opportunities
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Box>
                  {groupedByDrug.map((group, idx) => (
                    <Box
                      key={`drug-group-${group.drugName || ""}-${group.strength || ""}-${idx}`}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        border: "1px solid #E8E8E9",
                        backgroundColor: "#FFFFFF",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          px: 2,
                          pt: 2,
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              flexWrap: "wrap",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: "#101828",
                                fontSize: "20px",
                                lineHeight: 1.2,
                              }}
                            >
                              {capitalizeFirstLetter(group.drugName)}{" "}
                              <span
                                style={{
                                  color: "#403C6F",
                                  fontWeight: 700,
                                  fontSize: "20px",
                                }}
                              >
                                {group.programs[0]?.program?.strength || ""}
                              </span>
                            </Typography>
                            {group.programs[0]?.rejectionCode &&
                              group.programs[0]?.rejectionCode !== "N/A" && (
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "1.5px solid #D13D53",
                                    backgroundColor: "#FFF5F6",
                                    borderRadius: "40px",
                                    px: 1.5,
                                    py: 0.2,
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    color: "#D13D53",
                                    textTransform: "uppercase",
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {group.programs[0].rejectionCode}
                                </Box>
                              )}
                            {group.programs[0]?.paAction &&
                              group.programs[0]?.paAction !== "N/A" &&
                              (() => {
                                const isApproved = String(
                                  group.programs[0].paAction,
                                )
                                  .toLowerCase()
                                  .includes("approved");
                                return (
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      border: isApproved
                                        ? "1.5px solid #45B184"
                                        : "1.5px solid #D13D53",
                                      backgroundColor: isApproved
                                        ? "#E8F5E9"
                                        : "#FFF5F6",
                                      borderRadius: "40px",
                                      px: 1.5,
                                      py: 0.2,
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      color: isApproved ? "#2E7D32" : "#D13D53",
                                      textTransform: "uppercase",
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {group.programs[0].paAction}
                                  </Box>
                                );
                              })()}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: "4px",
                              mb: 2,
                              color: "#66638C",
                              mt: 1,
                              fontWeight: 400,
                              fontSize: "14px",
                              lineHeight: "16px",
                            }}
                          >
                            <span>
                              <span style={{ fontWeight: 500 }}>GPI14: </span>
                              <span
                                style={{ color: "#403C6F", fontWeight: 600 }}
                              >
                                {group.programs[0]?.medication?.drugGpi ||
                                  "N/A"}
                              </span>
                            </span>
                            <span>•</span>
                            <span>
                              <span style={{ fontWeight: 500 }}>NDC: </span>
                              <span
                                style={{ color: "#403C6F", fontWeight: 600 }}
                              >
                                {group.programs[0]?.medication?.drugNdc ||
                                  "N/A"}
                              </span>
                            </span>
                            <span>•</span>
                            <span>
                              <span style={{ fontWeight: 500 }}>Quantity:</span>{" "}
                              <span
                                style={{ color: "#403C6F", fontWeight: 600 }}
                              >
                                {group.programs[0]?.program?.metricQty || "-"}
                              </span>
                            </span>
                            <span>•</span>
                            <span>
                              <span style={{ fontWeight: 500 }}>
                                Days Supply:
                              </span>{" "}
                              <span
                                style={{ color: "#403C6F", fontWeight: 600 }}
                              >
                                {group.programs[0]?.program?.daySupply || "-"}
                              </span>
                            </span>
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              mb: 2,
                              color: "#66638C",
                              mt: 1,
                              fontWeight: 400,
                              fontSize: "14px",
                              lineHeight: "16px",
                            }}
                          >
                            Last Filled:{" "}
                            <span style={{ color: "#403C6F", fontWeight: 600 }}>
                              {group.programs[0]?.program?.lastFilled
                                ? formatDateTime(
                                    group.programs[0].program.lastFilled,
                                    false,
                                  )
                                : "-"}
                            </span>
                          </Typography>
                        </Box>
                      </Box>
                      <TableContainer
                        sx={{
                          width: "100%",
                          overflowX: "auto",
                          overflowY: "hidden",
                        }}
                      >
                        <Table
                          sx={{
                            tableLayout: "fixed",
                            width: "100%",
                            minWidth: "1150px",
                          }}
                        >
                          <TableHead>
                            <TableRow
                              sx={{
                                backgroundColor: "#CFCEDA",
                                minHeight: 28,
                                height: 28,
                              }}
                            >
                              <TableCell
                                sx={{
                                  py: 1.5,
                                  width: "20%",
                                  fontWeight: 550,
                                  fontSize: "16px",
                                  lineHeight: "24px",
                                  color: "#403C6F",
                                }}
                                align="left"
                              >
                                Latest Outcome
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 1.5,
                                  width: "25%",
                                  fontWeight: 550,
                                  fontSize: "16px",
                                  lineHeight: "24px",
                                  color: "#403C6F",
                                }}
                                align="left"
                              >
                                Program
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 1.5,
                                  width: "10%",
                                  fontWeight: 550,
                                  fontSize: "16px",
                                  lineHeight: "24px",
                                  color: "#403C6F",
                                }}
                                align="left"
                              >
                                Member Cost
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 1.5,
                                  width: "15%",
                                  fontWeight: 550,
                                  fontSize: "16px",
                                  lineHeight: "24px",
                                  color: "#403C6F",
                                }}
                                align="left"
                              >
                                Provider
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 1.5,
                                  width: "15%",
                                  fontWeight: 550,
                                  fontSize: "16px",
                                  lineHeight: "24px",
                                  color: "#403C6F",
                                }}
                                align="left"
                              >
                                Pharmacy
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 1.5,
                                  width: "18%",
                                  fontWeight: 550,
                                  fontSize: "16px",
                                  lineHeight: "24px",
                                  color: "#403C6F",
                                }}
                                align="left"
                              >
                                Action
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.programs.map((item, i) => (
                              <TableRow
                                key={item.program?.id + "-" + i}
                                sx={{ height: 64 }}
                              >
                                <TableCell
                                  sx={{
                                    width: "20%",
                                    maxWidth: 0,
                                    color: "#1B2F3F",
                                    fontWeight: 500,
                                    fontSize: "16px",
                                    lineHeight: "150%",
                                    overflow: "hidden",
                                  }}
                                >
                                  {item.recentActivity && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                          color: getRecentActivityColor(
                                            item.recentActivity,
                                          ),
                                          overflow: "hidden",
                                        }}
                                      >
                                        {item.recentActivity.reason ? (
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 500,
                                              fontSize: "16px",
                                              lineHeight: "150%",
                                              color: getRecentActivityColor(
                                                item.recentActivity,
                                              ),
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 0.5,
                                              overflow: "hidden",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            <span
                                              style={{
                                                fontWeight: 500,
                                                fontSize: "16px",
                                                flex: "0 0 auto",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                              }}
                                            >
                                              {item.recentActivity.status}
                                            </span>
                                            <Tooltip
                                              title={item.recentActivity.reason}
                                              arrow
                                              placement="top"
                                            >
                                              <span
                                                style={{
                                                  fontWeight: 400,
                                                  fontSize: "12px",
                                                  color: "#403C6F",
                                                  flex: "1 1 auto",
                                                  whiteSpace: "nowrap",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                }}
                                              >
                                                {` (${item.recentActivity.reason})`}
                                              </span>
                                            </Tooltip>
                                          </Typography>
                                        ) : (
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 500,
                                              fontSize: "16px",
                                              lineHeight: "150%",
                                              color: getRecentActivityColor(
                                                item.recentActivity,
                                              ),
                                              display: "-webkit-box",
                                              WebkitLineClamp: 2,
                                              WebkitBoxOrient: "vertical",
                                              overflow: "hidden",
                                            }}
                                          >
                                            {item.recentActivity.status}
                                          </Typography>
                                        )}
                                      </Box>
                                      {item.recentActivity.at && (
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "#1B2F3F",
                                            fontWeight: 500,
                                            fontSize: "16px",
                                            lineHeight: "150%",
                                            letterSpacing: "0%",
                                          }}
                                        >
                                          {item.recentActivity.at}
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    width: "25%",
                                    position: "relative",
                                    overflow: "visible",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      overflow: "visible",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        border: `1px solid ${
                                          iconColors?.borderColor
                                        }`,
                                        backgroundColor: iconColors?.bgColor,
                                        borderRadius: "40px",
                                        px: 1,
                                        py: 1,
                                        fontWeight: 500,
                                        width: "auto",
                                        height: "36px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {getCategoryIcon(
                                        item.category,
                                        item.status,
                                      )}
                                      <span
                                        style={{
                                          marginLeft: 8,
                                          color: "#403C6F",
                                          fontWeight: 700,
                                        }}
                                      >
                                        {formatCategoryWithVendor(item)}
                                      </span>
                                    </Box>

                                    {["biosimilars", "glp-1"].includes(
                                      (
                                        item.program?.programName || ""
                                      ).toLowerCase(),
                                    ) && (
                                      <Box
                                        className="alternatives-pill"
                                        sx={{
                                          py: 0.5,
                                          color: "#3D3470",
                                          fontWeight: 400,
                                          fontSize: "12px",
                                          cursor: "pointer",
                                          display: "flex",
                                          alignItems: "center",
                                          marginLeft: 1,
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (
                                            openAltDropdown === item.program?.id
                                          ) {
                                            setOpenAltDropdown(null);
                                            setAltAnchorEl(null);
                                          } else {
                                            setOpenAltDropdown(
                                              item.program?.id,
                                            );
                                            setAltAnchorEl(e.currentTarget);
                                          }
                                        }}
                                      >
                                        <span>Alternates</span>
                                        <span
                                          style={{
                                            marginLeft: 6,
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          {openAltDropdown ===
                                          item.program?.id ? (
                                            <KeyboardArrowUpIcon
                                              sx={{ color: "#3D3470" }}
                                            />
                                          ) : (
                                            <KeyboardArrowDownIcon
                                              sx={{ color: "#3D3470" }}
                                            />
                                          )}
                                        </span>
                                      </Box>
                                    )}
                                    <Popover
                                      open={
                                        openAltDropdown === item.program?.id
                                      }
                                      anchorEl={altAnchorEl}
                                      onClose={(e) => {
                                        e?.stopPropagation();
                                        setOpenAltDropdown(null);
                                        setAltAnchorEl(null);
                                      }}
                                      anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "left",
                                      }}
                                      transformOrigin={{
                                        vertical: "top",
                                        horizontal: "left",
                                      }}
                                      slotProps={{
                                        paper: {
                                          onClick: (e) => e.stopPropagation(),
                                          onMouseDown: (e) =>
                                            e.stopPropagation(),
                                          sx: {
                                            minWidth: 260,
                                            maxHeight: 200,
                                            overflowY: "auto",
                                            background: "#fff",
                                            borderRadius: "8px",
                                            boxShadow:
                                              "0px 4px 16px rgba(0, 0, 0, 0.12)",
                                            zIndex: 1200,
                                            p: 2,
                                          },
                                        },
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontWeight: 500,
                                          fontSize: "13px",
                                          color: "#6A7282",
                                          mb: 1,
                                        }}
                                      >
                                        Alternative Medications
                                      </Typography>
                                      <AlternativeDrugsList
                                        alternativeDrugs={
                                          item.medication?.alternativeDrugs
                                        }
                                      />
                                    </Popover>
                                  </Box>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    width: "10%",
                                    color: "#1B2F3F",
                                    fontWeight: 500,
                                    fontSize: "16px",
                                    lineHeight: "150%",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.cost}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    width: "15%",
                                    color: "#1B2F3F",
                                    fontWeight: 500,
                                    fontSize: "16px",
                                    lineHeight: "150%",
                                  }}
                                >
                                  {item.provider}
                                  <br />
                                  <Tooltip title="Phone" arrow>
                                    <img
                                      src={phoneIcon}
                                      alt="Phone"
                                      width={14}
                                      height={14}
                                    />
                                  </Tooltip>{" "}
                                  {item.providerPhone}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    width: "15%",
                                    color: "#1B2F3F",
                                    fontWeight: 500,
                                    fontSize: "16px",
                                    lineHeight: "150%",
                                  }}
                                >
                                  {item.pharmacy}
                                  <br />
                                  <Tooltip title="Phone" arrow>
                                    <img
                                      src={phoneIcon}
                                      alt="Phone"
                                      width={14}
                                      height={14}
                                    />
                                  </Tooltip>{" "}
                                  {item.pharmacyPhone}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    width: "18%",
                                    px: 1,
                                    color: "#1B2F3F",
                                    fontWeight: 500,
                                    fontSize: "16px",
                                    lineHeight: "150%",
                                  }}
                                >
                                  {item.category === "Copay Assist" &&
                                  item.program?.vendorName
                                    ?.trim()
                                    .toUpperCase() === "ASCELLA" ? (
                                    <CopaySubmissionCell
                                      item={item}
                                      canWrite={canWrite}
                                      submittedAscellaOpps={
                                        submittedAscellaOpps
                                      }
                                      openAscellaEnrollment={
                                        openAscellaEnrollment
                                      }
                                      showLoading={showLoading}
                                      memberId={memberId}
                                      memberData={memberData}
                                      setTrackOrdersData={setTrackOrdersData}
                                      setIsTrackAscella={setIsTrackAscella}
                                      setIsTrackEnrollmentOpen={
                                        setIsTrackEnrollmentOpen
                                      }
                                      refreshIcon={refreshIcon}
                                      getSvgFilter={getSvgFilter}
                                      hideLoading={hideLoading}
                                    />
                                  ) : (
                                    <VendorSubmissionCell
                                      item={item}
                                      canWrite={canWrite}
                                      openInternationalEnrollment={
                                        openInternationalEnrollment
                                      }
                                      openScriptCoEnrollment={
                                        openScriptCoEnrollment
                                      }
                                      setTrackOrdersData={setTrackOrdersData}
                                      setIsTrackEnrollmentOpen={
                                        setIsTrackEnrollmentOpen
                                      }
                                      refreshIcon={refreshIcon}
                                      getSvgFilter={getSvgFilter}
                                      onEditPap={handleEditPap}
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
          {activeTab === "clinical_programs" && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                mt: 1,
              }}
            >
              <ClinicalProgramsTable
                memberCgProgramsList={memberCgProgramsList}
                memberData={memberData}
                getNextScheduledAssessment={getNextScheduledAssessment}
                getCategoryIcon={getCategoryIcon}
                canWrite={canWrite}
                onAddProgramOutcome={handleOpenBiosimilarsOutcome}
              />
            </Box>
          )}
          {activeTab === "clinical_assessment_history" && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                overflow: "hidden",
              }}
            >
              <ClinicalAssessmentHistoryPanel
                memberId={memberId}
                memberData={memberData}
                onReopen={onReopenClinicalHistory}
                onCountChange={setClinicalAssessmentCount}
              />
            </Box>
          )}
          {activeTab === "outreach" && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                overflow: "hidden",
              }}
            >
              <OutreachHistoryPanel
                memberId={memberId}
                onLoadingChange={onOutreachLoadingChange}
                onCountChange={setOutreachCount}
              />
            </Box>
          )}
          {activeTab === "claims" && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                overflow: "hidden",
              }}
            >
              <MedicationHistory
                memberId={memberId}
                canWrite={canWrite}
                canDelete={canDelete}
                onLoadingChange={onMedicationLoadingChange}
                isShowPhiEnabled={isShowPhiEnabled}
                onCountChange={setMedicationCount}
              />
            </Box>
          )}
          {activeTab === "notes" && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                mt: 2,
                mb: 2,
                width: "100%",
                backgroundColor: "#ECECF1",
                borderRadius: 2,
                overflow: "hidden",
                p: { xs: 1, md: 1.5 },
                "& > *": {
                  flex: 1,
                  width: "100%",
                  minWidth: 0,
                },
              }}
            >
              <NotesCard
                notes={notes}
                title="Notes"
                selectedNoteType={selectedNoteType}
                searchQuery={searchQuery}
                inQuickview={true}
                onSearch={onSearchNotes}
                onNoteTypeChange={onNoteTypeChange}
                onTogglePin={onToggleNotePin}
                onEdit={onEditNote}
                canWrite={canWrite}
                setIsNoteModalOpen={setIsNoteModalOpen}
              />
            </Box>
          )}
          {activeTab === "escalations" && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                mt: 2,
                mb: 2,
                width: "100%",
                backgroundColor: "#ECECF1",
                borderRadius: 2,
                overflow: "hidden",
                p: { xs: 1, md: 1.5 },
                "& > *": {
                  flex: 1,
                  width: "100%",
                  minWidth: 0,
                },
              }}
            >
              <EscalationsCard
                escalations={escalations}
                onSelectEscalation={onSelectEscalation}
              />
            </Box>
          )}
        </Box>
        <RnVisitSchedulerModal
          open={isRnSchedulerModalOpen}
          onClose={() => setIsRnSchedulerModalOpen(false)}
          memberId={memberId}
          memberCgPlusPrograms={memberCgPlusPrograms}
          onScheduleSuccess={handleScheduleSuccess}
          memberDetails={memberData}
        />
        <Dialog
          open={isAppointmentScheduledPopupOpen}
          onClose={() => setIsAppointmentScheduledPopupOpen(false)}
          slotProps={{
            paper: {
              sx: {
                borderRadius: "16px",
                p: 2,
                maxWidth: "400px",
              },
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: "#10100F", pb: 1 }}>
            Appointment Scheduled
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: "#7F7C9F", mb: 2 }}>
              The RN visit has been successfully scheduled and is now available
              in the assigned RN's Member Outreach list.
            </Typography>
            {scheduledAppointmentInfo && (
              <Box
                sx={{
                  bgcolor: "#FAFAFF",
                  p: 1.5,
                  borderRadius: "8px",
                  border: "1px solid #E8E8E9",
                  mt: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: "#403C6F", display: "block" }}
                >
                  APPOINTMENT DETAILS:
                </Typography>
                <Typography variant="body2" sx={{ color: "#10100F", mt: 0.5 }}>
                  <strong>Clinician:</strong> {scheduledAppointmentInfo.rn}
                </Typography>
                <Typography variant="body2" sx={{ color: "#10100F" }}>
                  <strong>Program:</strong> {scheduledAppointmentInfo.program}
                </Typography>
                <Typography variant="body2" sx={{ color: "#10100F" }}>
                  <strong>Date & Time:</strong>{" "}
                  {scheduledAppointmentInfo.month + 1}/
                  {scheduledAppointmentInfo.day}/{scheduledAppointmentInfo.year}{" "}
                  @ {scheduledAppointmentInfo.time}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="contained"
              onClick={() => setIsAppointmentScheduledPopupOpen(false)}
              sx={{
                borderRadius: "24px",
                bgcolor: "#403C6F",
                color: "#FFFFFF",
                textTransform: "none",
                px: 3,
                "&:hover": {
                  bgcolor: "#2F2B59",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
        {!onOpenBiosimilarsOutcome && (
          <BiosimilarsOutcomeModal
            open={isLocalBiosimilarsModalOpen}
            onClose={() => setIsLocalBiosimilarsModalOpen(false)}
            onSuccess={async () => {
              try {
                const data = await fetchMemberClinicalPrograms(memberId);
                if (Array.isArray(data)) setClinicalPrograms(data);
                else if (Array.isArray(data?.content))
                  setClinicalPrograms(data.content);
                else if (Array.isArray(data?.programs))
                  setClinicalPrograms(data.programs);
                else if (Array.isArray(data?.data))
                  setClinicalPrograms(data.data);
              } catch (err) {
                console.error(err);
              }
            }}
            memberId={memberId}
          />
        )}
      </Paper>
    </Box>
  );
};

const AlternativeDrugsList = ({ alternativeDrugs }) => {
  if (alternativeDrugs && alternativeDrugs.length > 0) {
    return alternativeDrugs.map((alt, idx) => (
      <Box
        key={`alt-drug-${alt.altNdc || ""}-${alt.altDrugName || ""}-${idx}`}
        sx={{ mb: 1, p: 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "14px",
            color: "#0A0A0A",
          }}
        >
          {alt.altDrugName}
        </Typography>
      </Box>
    ));
  }
  return (
    <Typography
      sx={{ fontSize: "13px", color: "#66638C" }}
      onClick={(e) => e.stopPropagation()}
    >
      No alternatives available.
    </Typography>
  );
};

AlternativeDrugsList.propTypes = {
  alternativeDrugs: PropTypes.arrayOf(
    PropTypes.shape({
      altNdc: PropTypes.string,
      altDrugName: PropTypes.string,
    }),
  ),
};
// Ensure this is the only default export in the file

OpportunitiesTable.propTypes = {
  onOpenClinicalHistory: PropTypes.func,
  onReopenClinicalHistory: PropTypes.func,
  onStartClinicalNote: PropTypes.func,
  clinicalNotePrograms: PropTypes.array,
  programMap: PropTypes.object,
  referenceData: PropTypes.any,
  onScheduleSuccess: PropTypes.func,
  showMonitoringReason: PropTypes.bool,
  setDispositionOpportunity: PropTypes.func,
  memberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setProgramOptions: PropTypes.func,
  setProgramDrugMap: PropTypes.func,
  fromMemberMonitoring: PropTypes.bool,
  canWrite: PropTypes.bool,
  pageSize: PropTypes.number,
  onOpenDisposition: PropTypes.func,
  setProgramMap: PropTypes.func,
  onLoadingChange: PropTypes.func,
  isShowPhiEnabled: PropTypes.bool,
  canDelete: PropTypes.bool,
  onMedicationLoadingChange: PropTypes.func,
  onOutreachLoadingChange: PropTypes.func,
  notes: PropTypes.arrayOf(PropTypes.object),
  selectedNoteType: PropTypes.arrayOf(PropTypes.string),
  searchQuery: PropTypes.string,
  onNoteTypeChange: PropTypes.func,
  onSearchNotes: PropTypes.func,
  onToggleNotePin: PropTypes.func,
  onEditNote: PropTypes.func,
  setIsNoteModalOpen: PropTypes.func,
  setIsEscalationModalOpen: PropTypes.func,
  escalations: PropTypes.array,
  onSelectEscalation: PropTypes.func,
  showHeaderActions: PropTypes.bool,
  externalOutcomeTriggerKey: PropTypes.number,
  activateEscalationsTabKey: PropTypes.number,
  onTabChange: PropTypes.func,
  memberData: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  onGetCopayCard: PropTypes.func,
  onCopayOpportunityFound: PropTypes.func,
  onCopayOpportunitiesFound: PropTypes.func,
  onTotalOpportunitiesChange: PropTypes.func,
  onOpenBiosimilarsOutcome: PropTypes.func,
};

const vendorNameIncludes = (item, text) =>
  item.vendor?.vendorName?.toUpperCase().includes(text) ||
  item.vendorName?.toUpperCase().includes(text);

const formatSavings = (amount) =>
  amount !== null && amount !== undefined
    ? `${Number(amount).toFixed(2)}`
    : "-";

const isPositiveAmount = (amount) =>
  amount !== null &&
  amount !== undefined &&
  amount !== "" &&
  Number.parseFloat(amount) > 0;

const buildOrdersToSend = (item, vendorName) => {
  if (item.vendorReferenceId) {
    return [
      {
        orderId: item.vendorReferenceId,
        status: "Submitted",
        vendor: vendorName,
      },
    ];
  }
  if (item.orders && item.orders.length > 0) {
    return item.orders.map((o) => ({
      ...o,
      vendor: o.vendor || vendorName,
    }));
  }
  const fallbackOrderId = item.opportunityId ? `#${item.opportunityId}` : "";
  return [
    {
      orderId: fallbackOrderId,
      status: "Submitted",
      vendor: vendorName,
    },
  ];
};

const getPapSavingsButtonSx = (hasPapValues) => ({
  color: hasPapValues ? "#45B184" : "#403C6F",
  textTransform: "none",
  border: hasPapValues ? "1px solid #45B184" : "1px solid #E8E8E9",
  borderRadius: 20,
  fontWeight: hasPapValues ? 600 : 400,
  fontSize: "15px",
  px: 2,
  py: 0.8,
  background: "#ffffff",
  "&:hover": {
    backgroundColor: hasPapValues
      ? "rgba(69, 177, 132, 0.04)"
      : "rgba(70, 64, 90, 0.04)",
    borderColor: hasPapValues ? "#45B184" : "#403C6F",
  },
  minWidth: "auto",
  whiteSpace: "nowrap",
});

const VendorSubmissionCell = ({
  item,
  canWrite,
  openInternationalEnrollment,
  openScriptCoEnrollment,
  setTrackOrdersData,
  setIsTrackEnrollmentOpen,
  refreshIcon,
  getSvgFilter,
  onEditPap,
}) => {
  const isPap = item.category === "PAP";
  const isScriptCo = vendorNameIncludes(item, "SCRIPTCO");
  const isGrm = vendorNameIncludes(item, "GRM");

  // item.vendor/vendorName come back null for International Sourcing even
  // after a vendor order has actually been placed (the backend never tags
  // the opportunity with a vendor name for that program), so vendorName
  // matching alone can't be trusted to gate this cell - a populated
  // vendorReferenceId is independent proof a submission already happened
  // and the Edit/Track-order buttons below need to render regardless.
  if (!isGrm && !isScriptCo && !isPap && !item.vendorReferenceId) {
    return <>{"-"}</>;
  }

  const handleVendorClick = () => {
    if (isScriptCo && openScriptCoEnrollment) {
      openScriptCoEnrollment(item);
    } else {
      openInternationalEnrollment(item);
    }
  };

  const vendorButtonLabel = isScriptCo ? "Submit to ScriptCo" : "Submit to GRM";

  const formattedPlanSavings = formatSavings(item.planSavings);
  const formattedMemberSavings = formatSavings(item.memberSavings);

  const savingsTooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: "#FFFFFF",
          mb: 1,
          borderBottom: "1px solid rgba(255,255,255,0.15)",
          pb: 0.5,
        }}
      >
        PAP Savings Summary
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto auto",
          gap: 1,
          columnGap: 3,
        }}
      >
        <Typography sx={{ fontSize: "11px", color: "#D7D5E4" }}>
          Total PAP Value
        </Typography>
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: "#FFFFFF",
            textAlign: "right",
          }}
        >
          {formattedPlanSavings}
        </Typography>

        <Typography sx={{ fontSize: "11px", color: "#D7D5E4" }}>
          Member Copay × # of fills
        </Typography>
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: "#FFFFFF",
            textAlign: "right",
          }}
        >
          {formattedMemberSavings}
        </Typography>
      </Box>
    </Box>
  );

  if (isPap) {
    const hasPapValues =
      isPositiveAmount(item.planSavings) ||
      isPositiveAmount(item.memberSavings);

    return (
      <Box sx={{ display: "flex", gap: 1 }}>
        <Tooltip
          title={savingsTooltipContent}
          arrow
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: "#2e2a56",
                borderRadius: "8px",
                p: 1.5,
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
              },
            },
            arrow: {
              sx: {
                color: "#2e2a56",
              },
            },
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => onEditPap(item)}
            sx={getPapSavingsButtonSx(hasPapValues)}
          >
            PAP Savings
          </Button>
        </Tooltip>
      </Box>
    );
  }

  if (item.vendorReferenceId) {
    return (
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleVendorClick}
          sx={{
            color: "#403C6F",
            textTransform: "none",
            border: "1px solid #E8E8E9",
            borderRadius: 2,
            fontWeight: 400,
            fontSize: "15px",
            px: 2,
            py: 0.8,
            background: "#ffffff",
            "&:hover": {
              backgroundColor: "rgba(70, 64, 90, 0.04)",
              borderColor: "#403C6F",
            },
            minWidth: "auto",
          }}
        >
          <EditOutlinedIcon
            sx={{
              color: canWrite ? "#6E6D92" : "#999893",
              fontSize: "20px",
            }}
          />
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            const vendorName = isScriptCo ? "ScriptCo" : "GRM";
            const ordersToSend = buildOrdersToSend(item, vendorName);
            setTrackOrdersData(ordersToSend);
            setIsTrackEnrollmentOpen(true);
          }}
          sx={{
            color: "#403C6F",
            textTransform: "none",
            border: "1px solid #E8E8E9",
            borderRadius: 2,
            fontWeight: 400,
            fontSize: "15px",
            px: 2,
            py: 0.8,
            background: "#ffffff",
            "&:hover": {
              backgroundColor: "rgba(70, 64, 90, 0.04)",
              borderColor: "#403C6F",
            },
            minWidth: "auto",
          }}
        >
          <img
            src={refreshIcon}
            alt="Refresh"
            style={{
              width: 24,
              height: 24,
              filter: getSvgFilter(canWrite ? "#6E6D92" : "#999893"),
            }}
          />
        </Button>
      </Box>
    );
  }

  const isEnrollmentInProgress =
    (item.recentActivity?.status || "").trim().toLowerCase() ===
    "enrollment in progress";

  if (!isEnrollmentInProgress) {
    return (
      <Box sx={{ display: "flex", gap: 1 }}>
        <Tooltip
          title='Add an "Enrollment in Progress" disposition to enable vendor submission.'
          arrow
          placement="top"
        >
          <span>
            <Button
              variant="outlined"
              size="small"
              disabled
              sx={{
                color: "#999893",
                textTransform: "none",
                border: "1px solid #E8E8E9",
                borderRadius: 20,
                fontWeight: 400,
                fontSize: "15px",
                px: 2,
                py: 0.8,
                background: "#ffffff",
                pointerEvents: "none",
                whiteSpace: "nowrap",
              }}
            >
              {vendorButtonLabel}
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Button
        variant="outlined"
        size="small"
        onClick={handleVendorClick}
        sx={{
          color: "#403C6F",
          textTransform: "none",
          border: "1px solid #E8E8E9",
          borderRadius: 20,
          fontWeight: 400,
          fontSize: "15px",
          px: 2,
          py: 0.8,
          background: "#ffffff",
          "&:hover": {
            backgroundColor: "rgba(70, 64, 90, 0.04)",
            borderColor: "#403C6F",
          },
          minWidth: "auto",
          whiteSpace: "nowrap",
        }}
      >
        {vendorButtonLabel}
      </Button>
    </Box>
  );
};

VendorSubmissionCell.propTypes = {
  item: PropTypes.object.isRequired,
  canWrite: PropTypes.bool,
  openInternationalEnrollment: PropTypes.func.isRequired,
  openScriptCoEnrollment: PropTypes.func,
  setTrackOrdersData: PropTypes.func.isRequired,
  setIsTrackEnrollmentOpen: PropTypes.func.isRequired,
  refreshIcon: PropTypes.string.isRequired,
  getSvgFilter: PropTypes.func.isRequired,
  onEditPap: PropTypes.func.isRequired,
};

const CopaySubmissionCell = ({
  item,
  canWrite,
  submittedAscellaOpps,
  openAscellaEnrollment,
  showLoading,
  memberId,
  memberData,
  setTrackOrdersData,
  setIsTrackAscella,
  setIsTrackEnrollmentOpen,
  refreshIcon,
  getSvgFilter,
  hideLoading,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        alignItems: "flex-start",
      }}
    >
      {item.isSubmitted ||
      item.vendorReferenceId ||
      submittedAscellaOpps.includes(item.opportunityId) ||
      submittedAscellaOpps.includes(item.memberProgramId) ? (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => openAscellaEnrollment(item)}
            sx={{
              color: "#403C6F",
              textTransform: "none",
              border: "1px solid #E8E8E9",
              borderRadius: 2,
              fontWeight: 400,
              fontSize: "15px",
              px: 2,
              py: 0.8,
              background: "#ffffff",
              "&:hover": {
                backgroundColor: "rgba(70, 64, 90, 0.04)",
                borderColor: "#403C6F",
              },
              minWidth: "auto",
            }}
          >
            <VisibilityOutlinedIcon
              sx={{
                color: canWrite ? "#6E6D92" : "#999893",
                fontSize: "20px",
              }}
            />
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={async () => {
              try {
                showLoading();
                const oppId =
                  item.opportunityId || item.memberProgramId || item.programId;
                const trackResponse = await trackAscellaOrder(memberId, oppId);

                let finalOrderId = "N/A";
                if (trackResponse?.orderId) {
                  finalOrderId = `#${trackResponse.orderId}`;
                } else if (trackResponse?.id) {
                  finalOrderId = `#${trackResponse.id}`;
                } else if (oppId) {
                  finalOrderId = `#${oppId}`;
                }

                const mappedOrder = {
                  orderId: finalOrderId,
                  trackingId:
                    trackResponse?.trackingNum ||
                    trackResponse?.trackingId ||
                    trackResponse?.items?.[0]?.shippingTrackingNumber ||
                    "N/A",
                  enrollmentId: trackResponse?.customerAccount?.id
                    ? `ENR-${trackResponse.customerAccount.id}`
                    : trackResponse?.enrollmentId || "N/A",
                  memberName:
                    memberData?.name ||
                    (memberData?.firstName && memberData?.lastName
                      ? `${memberData.firstName} ${memberData.lastName}`
                      : item.member?.fullName || ""),
                  vendor: trackResponse?.vendor || "ascella",
                  submittedOn:
                    trackResponse?.dateCreated ||
                    trackResponse?.submittedOn ||
                    new Date().toISOString(),
                  rawSubmittedOn:
                    trackResponse?.dateCreated ||
                    trackResponse?.submittedOn ||
                    new Date().toISOString(),
                  status:
                    trackResponse?.syncStatus ||
                    trackResponse?.status ||
                    trackResponse?.items?.[0]?.status ||
                    "Submitted",
                  trackingHistory: trackResponse?.trackingHistory || [],
                };
                setTrackOrdersData([mappedOrder]);
                setIsTrackAscella(true);
                setIsTrackEnrollmentOpen(true);
              } catch (error) {
                console.error("Failed to track Ascella order:", error);
                const oppId =
                  item.opportunityId || item.memberProgramId || item.programId;
                const fallbackOrder = {
                  orderId: oppId ? `#${oppId}` : "#12345678899",
                  trackingId: "N/A",
                  enrollmentId: "N/A",
                  memberName:
                    memberData?.name ||
                    (memberData?.firstName && memberData?.lastName
                      ? `${memberData.firstName} ${memberData.lastName}`
                      : item.member?.fullName || ""),
                  vendor: "ascella",
                  submittedOn: new Date().toISOString(),
                  status: "Submitted",
                  trackingHistory: [],
                };
                setTrackOrdersData([fallbackOrder]);
                setIsTrackAscella(true);
                setIsTrackEnrollmentOpen(true);
              } finally {
                hideLoading();
              }
            }}
            sx={{
              color: "#403C6F",
              textTransform: "none",
              border: "1px solid #E8E8E9",
              borderRadius: 2,
              fontWeight: 400,
              fontSize: "15px",
              px: 2,
              py: 0.8,
              background: "#ffffff",
              "&:hover": {
                backgroundColor: "rgba(70, 64, 90, 0.04)",
                borderColor: "#403C6F",
              },
              minWidth: "auto",
            }}
          >
            <img
              src={refreshIcon}
              alt="Refresh"
              style={{
                width: 24,
                height: 24,
                filter: getSvgFilter(canWrite ? "#6E6D92" : "#999893"),
              }}
            />
          </Button>
        </Box>
      ) : (
        <Tooltip
          title={
            item.recentActivity?.status?.trim().toLowerCase() ===
            "enrollment in progress"
              ? ""
              : 'Add an "Enrollment in Progress" disposition to enable vendor submission.'
          }
          arrow
          placement="top"
        >
          <span>
            <Button
              disabled={
                item.recentActivity?.status?.trim().toLowerCase() !==
                "enrollment in progress"
              }
              variant="outlined"
              size="small"
              onClick={() => openAscellaEnrollment(item)}
              sx={{
                color: "#403C6F",
                textTransform: "none",
                border: "1px solid #E8E8E9",
                borderRadius: 20,
                fontWeight: 400,
                fontSize: "15px",
                px: 2,
                py: 0.8,
                background: "#ffffff",
                "&:hover": {
                  backgroundColor: "rgba(70, 64, 90, 0.04)",
                  borderColor: "#403C6F",
                },
                minWidth: "auto",
                whiteSpace: "nowrap",
              }}
            >
              Submit to Ascella
            </Button>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

CopaySubmissionCell.propTypes = {
  item: PropTypes.object.isRequired,
  canWrite: PropTypes.bool,
  submittedAscellaOpps: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ).isRequired,
  openAscellaEnrollment: PropTypes.func.isRequired,
  showLoading: PropTypes.func.isRequired,
  memberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  memberData: PropTypes.object,
  setTrackOrdersData: PropTypes.func.isRequired,
  setIsTrackAscella: PropTypes.func.isRequired,
  setIsTrackEnrollmentOpen: PropTypes.func.isRequired,
  refreshIcon: PropTypes.string.isRequired,
  getSvgFilter: PropTypes.func.isRequired,
  hideLoading: PropTypes.func.isRequired,
};

export default OpportunitiesTable;
